import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { createTemplateFn } from "../../../../redux/reducers/TemplateField/templates/server";
import {
  createJob,
  createTemplate,
  getFragment,
  getTemplateByName,
  getTable,
  getTemplate,
  updateTemplate,
} from "src/docuclipper/api";
import {
  Rectangle,
  BACKEND_WIDTH,
  BACKEND_HEIGHT,
  BACKEND_PORTRAIT_WIDTH,
  BACKEND_PORTRAIT_HEIGHT,
  BACKEND_LANDSCAPE_WIDTH,
  BACKEND_LANDSCAPE_HEIGHT,
  getBackendDimensions,
  toBackendCoordinates,
  toDisplayCoordinates,
  toBackendCoordinatesImproved,
  toDisplayCoordinatesImproved,
  drawRectangles,
} from "./pdfHelpers";
import { useLocation, useHistory } from "react-router-dom";
import queryString from "query-string";
import useBankStatementPDF from "../../DataArchive/TransactionManager/useBankStatementPDF";
import { useDispatch, useSelector } from "react-redux";
import { createAlert } from "../../../../redux/reducers/Alerts";
import { setJobData } from "../../../../redux/reducers/JobData";
import { setData as setJobDocumentData } from "../../../../redux/reducers/JobDocument";
import { templateSetData } from "../../../../redux/reducers/TemplateField";

import { RootState } from "../../../../redux/store";
import { TemplateFieldState } from "../../../../redux/reducers/TemplateFieldTypes";
import { ReduxState } from "src/redux";
import { Document } from "src/docuclipper/DocuclipperTypes";
import { useL1L2Params } from "src/hooks/useL1L2Params";
import ClipLoader from "react-spinners/ClipLoader";
import { ColumnConfig } from "./ColumnConfig";
import { Button, Modal, Form, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CsvToHtmlTable } from "../../../../react-csv-to-table";
import { parseCsvToRowsAndColumn } from "../../../../react-csv-to-table/utils";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

type Props = {};

type FieldType = "text" | "table" | "aiPrompt";
type TextFieldType = "text" | "number" | "date";

type Box = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fieldType?: FieldType;
  textFieldType?: TextFieldType;
  columns?: number;
  columnPositions?: number[]; // Store as percentages (0-100)
  columnConfigs?: {
    name: string;
    type: TextFieldType;
    columnWidthPercentage?: number;
  }[];
  isDragging?: boolean;
  isResizing?: boolean;
  pageNumber?: number;
  appliesToAllPages?: boolean;
  name?: string;
  isConfiguring?: boolean; // Add this flag
  /** For fieldType === "aiPrompt": no region; extraction by AI from prompt */
  aiPrompt?: { prompt: string };
};

type ConfigStep =
  | "fieldType"
  | "textFieldType"
  | "columns"
  | "columnConfig"
  | "pageConfig"
  | "complete";

const DrawRectangleV3_: React.FC<Props> = ({}) => {
  const location = useLocation();
  const query = queryString.parse(location.search);
  const documentId = query.documentId as string;
  const templateId = query.templateId
    ? parseInt(query.templateId as string, 10)
    : null;

  const history = useHistory();

  const {
    templateFields,
    page,
    selectedFieldId,
    canvasMode,
    template: templateObj,
    stage,
  } = useSelector(
    (state: ReduxState) => state.TemplateField as TemplateFieldState,
  );

  const template = templateObj.template;

  const { files } = useSelector((state: ReduxState) => state.Files);
  let documentRow: Document | undefined;
  if (files.length > 0) {
    documentRow = files[0].document;
  }

  // Add state for template document ID
  const [templateDocumentId, setTemplateDocumentId] = useState<string | null>(
    null,
  );

  // Update the PDF loading hook to use templateDocumentId when available
  const { pdfURL: imageSrc } = useBankStatementPDF({
    selectedDocumentId: templateId
      ? templateDocumentId
      : documentId || documentRow?.id,
  });

  // Document ID for fragment/table API: when editing use template's document, when creating use URL or uploaded file
  const effectiveDocumentId =
    templateId && templateDocumentId
      ? templateDocumentId
      : documentId || documentRow?.id?.toString();

  // console.log({ templateDocumentId, documentId, documentRow, imageSrc });

  // Add minimal check for document availability

  // console.log({ imageSrc });

  // console.log("DrawRectangleV3_ component mounted with imageSrc:", imageSrc);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rectCanvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [viewport, setViewport] = useState<pdfjsLib.PageViewport | null>(null);
  const drawingBoxRef = useRef<Box | null>(null);
  const pdfImageRef = useRef<ImageData | null>(null);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [configStep, setConfigStep] = useState<ConfigStep>("complete");
  const [configuringBox, setConfiguringBox] = useState<Box | null>(null);
  const [columnCount, setColumnCount] = useState<number>(2);
  const [isDraggingColumn, setIsDraggingColumn] = useState<number | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<
    "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w" | null
  >(null);
  const [moveStartPos, setMoveStartPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [originalBoxPos, setOriginalBoxPos] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [pageThumbnails, setPageThumbnails] = useState<
    Array<{ pageNumber: number; thumbnail: string }>
  >([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [showAddAiModal, setShowAddAiModal] = useState(false);
  const [showAddFieldChoiceOverlay, setShowAddFieldChoiceOverlay] =
    useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [extractFromCurrentLoading, setExtractFromCurrentLoading] =
    useState(false);
  const [aiFieldForm, setAiFieldForm] = useState({
    name: "",
    prompt: "",
    dataType: "text" as "text" | "table",
    appliesToAllPages: false,
  });
  const [popoverPosition, setPopoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [extractedData, setExtractedData] = useState<string | null>(null);
  const [aiPreviewLoading, setAiPreviewLoading] = useState(false);
  const [aiPreviewText, setAiPreviewText] = useState<string | null>(null);
  const [aiPreviewError, setAiPreviewError] = useState<string | null>(null);
  /** Backend coords (x0,y0,x1,y1) from AI preview response; used to draw rectangle on canvas. pageNumber is 1-based. */
  const [aiPreviewRect, setAiPreviewRect] = useState<{
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    pageNumber: number;
  } | null>(null);
  const lastCreatedAiFieldIdRef = useRef<string | null>(null);
  const prevSelectedBoxIdRef = useRef<string | null>(null);
  const isInitialFieldLoadRef = useRef<boolean>(true);
  const [isTemplateNameValid, setIsTemplateNameValid] = useState(true);
  const templateNameCheckTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const createdInitialTemplateRef = useRef(false);
  const persistTemplateTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const templateIdPersistRef = useRef<string | number | null>(null);
  const getPayloadPersistRef = useRef<
    () => {
      name: string;
      template: string;
      version: string;
    }
  >(() => ({ name: "", template: "{}", version: "v2" }));
  const [hasRendered, setHasRendered] = useState(false);
  const [showExtractionWarningModal, setShowExtractionWarningModal] =
    useState(false);
  const [pendingExtractionAction, setPendingExtractionAction] = useState<
    "saveAndStart" | "extractFromDocument" | null
  >(null);

  // When there are no boxes, show the "add field" choice overlay again
  useEffect(() => {
    if (boxes.length === 0) {
      setShowAddFieldChoiceOverlay(true);
    }
  }, [boxes.length]);

  const POPOVER_WIDTH = 300;
  const POPOVER_HEIGHT = 260; // Approximate, adjust as needed
  const POPOVER_GAP = 200;

  const dispatch = useDispatch();
  const { l1, l2 } = useL1L2Params();

  // Load PDF document and generate thumbnails
  useEffect(() => {
    let loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;

    const loadPdf = async () => {
      try {
        // console.log("Starting PDF load from:", imageSrc);
        loadingTask = pdfjsLib.getDocument(imageSrc);
        // console.log("Loading task created");
        const pdf = await loadingTask.promise;
        // console.log("PDF loaded successfully:", pdf);
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setIsPdfLoading(false);

        // Generate thumbnails for all pages
        const thumbnails: Array<{ pageNumber: number; thumbnail: string }> = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.2, rotation: 0 }); // Small scale for thumbnails
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context!,
            viewport: viewport,
          }).promise;

          thumbnails.push({
            pageNumber: i,
            thumbnail: canvas.toDataURL(),
          });
        }
        setPageThumbnails(thumbnails);
      } catch (error) {
        if (error instanceof Error && error.message.includes("cancelled")) {
          // console.log("PDF loading was cancelled");
        } else {
          console.error("Error loading PDF:", error);
        }
        setIsPdfLoading(false);
      }
    };

    if (!imageSrc) {
      // console.log("No imageSrc provided, skipping PDF load");
      return;
    }

    setIsPdfLoading(true);
    loadPdf();

    // Cleanup function to cancel any ongoing loading task
    return () => {
      if (loadingTask) {
        // console.log("Cancelling PDF loading task");
        loadingTask.destroy();
      }
    };
  }, [imageSrc]);

  // Add effect to render page when PDF document is loaded
  useEffect(() => {
    if (pdfDoc && !isPdfLoading) {
      // console.log("PDF document loaded, triggering render");
      renderPage();
    }
  }, [pdfDoc, isPdfLoading]);

  // Propose an initial unique template name when creating a new template
  useEffect(() => {
    if (templateId || !templateObj) return;
    const currentName =
      (templateObj.template && templateObj.template.name) || "";
    if (currentName.trim()) return;

    let cancelled = false;
    const proposeName = async () => {
      for (let n = 1; n < 1000; n++) {
        if (cancelled) return;
        const name = n === 1 ? "Untitled template" : `Untitled template ${n}`;
        try {
          const existing = await getTemplateByName(name);
          const list = Array.isArray(existing) ? existing : [];
          if (list.length === 0) {
            if (!cancelled) dispatch(templateSetData({ template: { name } }));
            return;
          }
        } catch {
          if (!cancelled) dispatch(templateSetData({ template: { name } }));
          return;
        }
      }
    };
    proposeName();
    return () => {
      cancelled = true;
    };
  }, [templateId, templateObj?.template?.name, dispatch]);

  // Create template as soon as we have a name (and document) so edits aren't lost
  useEffect(() => {
    if (templateId || !template?.name?.trim()) return;
    const docId = documentRow?.id || documentId;
    if (!docId || createdInitialTemplateRef.current) return;

    createdInitialTemplateRef.current = true;
    const templateData = {
      name: template.name,
      metadataFields: [],
      logoURL: "",
      intuitNumber: "",
      visibility: "private",
      category: "",
      type: "FreeForm",
      description: "",
      version: "v2",
      template: JSON.stringify({ rectangles: [] }),
      templateDocumentId: docId,
      createJob: false,
      l1: l1 || "",
      l2: l2 || "",
    };
    createTemplate(templateData)
      .then((newTemplate: any) => {
        const id = newTemplate?.id ?? newTemplate?.template?.id;
        if (id != null && history && location) {
          const params = new URLSearchParams(location.search);
          params.set("templateId", String(id));
          history.replace(`${location.pathname}?${params.toString()}`);
          dispatch(templateSetData({ template: { id } }));
        }
      })
      .catch(() => {
        createdInitialTemplateRef.current = false;
      });
  }, [
    templateId,
    template?.name,
    documentId,
    documentRow?.id,
    dispatch,
    history,
    location,
    l1,
    l2,
  ]);

  // Keep templateId in the URL when editing so refresh works
  useEffect(() => {
    if (templateId == null || !history || !location) return;
    const params = new URLSearchParams(location.search);
    if (params.get("templateId") !== String(templateId)) {
      params.set("templateId", String(templateId));
      history.replace(`${location.pathname}?${params.toString()}`);
    }
  }, [templateId, history, location]);

  // Add effect for template loading
  useEffect(() => {
    const loadTemplate = async () => {
      if (!templateId) return;

      try {
        const template = await getTemplate(templateId);
        const templateObj = JSON.parse(template.template);

        // Persist template name (and id) in Redux so it shows after refresh
        dispatch(
          templateSetData({
            template: {
              name: template.name ?? "",
              id: template.id,
            },
          }),
        );

        // Store the template's document ID
        setTemplateDocumentId(template.templateDocumentId?.toString() || null);

        // Convert template rectangles to our box format
        const newBoxes = templateObj.rectangles.map((rect: any) => {
          if (rect.locationType === "aiPrompt" && rect.aiPrompt) {
            const isTable =
              rect.fieldType === "table" ||
              (rect.tableConfig?.columns?.length || 0) > 0;
            const columns = rect.tableConfig?.columns || [];
            return {
              id: rect.id,
              x: 0,
              y: 0,
              width: 0,
              height: 0,
              fieldType: "aiPrompt" as const,
              name: rect.name,
              aiPrompt: { prompt: rect.aiPrompt.prompt },
              pageNumber:
                rect.pages.pagesType === "allPages"
                  ? undefined
                  : (rect.pages.page || 0) + 1,
              appliesToAllPages: rect.pages.pagesType === "allPages",
              ...(isTable
                ? {
                    columns: columns.length,
                    columnPositions: columns
                      .map((c: any) => c.columnWidthPercentage)
                      .filter((_: any, i: number) => i > 0),
                    columnConfigs: columns.map((col: any) => ({
                      name: col.name || `Column ${(col.index || 0) + 1}`,
                      type: col.fieldType || "text",
                      columnWidthPercentage: col.columnWidthPercentage,
                    })),
                  }
                : {}),
            };
          }

          // Convert backend coordinates to display coordinates
          const displayCoords = toDisplayCoordinatesImproved(
            rect.fixedLocation.pageLocation.x0,
            rect.fixedLocation.pageLocation.y0,
            rect.fixedLocation.pageLocation.x1 -
              rect.fixedLocation.pageLocation.x0,
            rect.fixedLocation.pageLocation.y1 -
              rect.fixedLocation.pageLocation.y0,
            viewport,
            canvasRef.current?.width || 0,
            canvasRef.current?.height || 0,
          );

          // For tables, ensure proper column setup (backend uses "tables"; accept "ocrtables" for backward compatibility)
          const isTable =
            rect.toolType === "tables" || rect.toolType === "ocrtables";
          const columns = isTable ? rect.tableConfig?.columns || [] : [];
          const columnCount = isTable ? columns.length : 2;
          const columnPositions = isTable
            ? columns.map((col: any) => col.columnWidthPercentage)
            : [];
          const columnConfigs = isTable
            ? columns.map((col: any) => ({
                name: col.name || `Column ${col.index + 1}`,
                type: col.fieldType || "text",
                columnWidthPercentage: col.columnWidthPercentage,
              }))
            : [];
          return {
            id: rect.id,
            x: displayCoords.x,
            y: displayCoords.y,
            width: displayCoords.width,
            height: displayCoords.height,
            fieldType: isTable ? "table" : "text",
            pageNumber: rect.pages.page + 1,
            appliesToAllPages: rect.pages.pagesType === "allPages",
            columns: columnCount,
            columnPositions,
            columnConfigs,
            name: rect.name,
          };
        });

        setBoxes(newBoxes);

        // Select the first box if available
        if (newBoxes.length > 0) {
          setSelectedBoxId(newBoxes[0].id);
        }
      } catch (error) {
        console.error("Error loading template:", error);
      }
    };

    loadTemplate();
  }, [templateId, viewport, dispatch]);

  const renderPage = async () => {
    // Basic validation
    if (!pdfDoc || !canvasRef.current) {
      // console.log("Missing requirements:", { hasPdf: !!pdfDoc, hasCanvas: !!canvasRef.current });
      return;
    }

    // console.log("Starting renderPage for page:", currentPage);

    try {
      // 1. Cancel any ongoing render and wait for it to finish
      if (renderTaskRef.current) {
        // console.log("Cancelling previous render");
        try {
          await renderTaskRef.current.cancel();
        } catch (e) {
          // Ignore cancellation errors
        }
        renderTaskRef.current = null;
      }

      // 2. Get the page
      // console.log("Getting page:", currentPage);
      const page = await pdfDoc.getPage(currentPage);
      // console.log("Got page, rotate:", page.rotate);

      // 3. Get the viewport (preserve original aspect ratio)
      const viewport = page.getViewport({ scale });
      // console.log("Viewport dimensions:", { width: viewport.width, height: viewport.height, scale });

      // 4. Setup main canvas
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) {
        // console.error("No canvas context");
        return;
      }

      // 5. Set canvas dimensions to match PDF (preserve aspect ratio)
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      // console.log("Canvas setup complete:", {
      //   canvasWidth: canvas.width,
      //   canvasHeight: canvas.height,
      //   styleWidth: canvas.style.width,
      //   styleHeight: canvas.style.height
      // });

      // 6. Setup rectangle canvas to match
      if (rectCanvasRef.current) {
        rectCanvasRef.current.width = viewport.width;
        rectCanvasRef.current.height = viewport.height;
        rectCanvasRef.current.style.width = `${viewport.width}px`;
        rectCanvasRef.current.style.height = `${viewport.height}px`;
      }

      // 7. Render PDF page
      // console.log("Starting PDF render");
      const renderTask = page.render({
        canvasContext: context,
        viewport: viewport,
      });

      // 8. Store reference and wait for completion
      renderTaskRef.current = renderTask;
      await renderTask.promise;
      // console.log("PDF render completed successfully");

      // 9. Update state and store image data
      setViewport(viewport);
      setHasRendered(true);
      pdfImageRef.current = context.getImageData(
        0,
        0,
        viewport.width,
        viewport.height,
      );
      // console.log("Stored PDF image data, size:", pdfImageRef.current.data.length);

      // 10. Draw boxes if any exist
      // console.log("About to call drawBoxes, visible boxes count:", visibleBoxes.length);
      drawBoxes();
      // console.log("drawBoxes completed");
    } catch (error) {
      console.error("Render error:", error);
      if (error instanceof Error && !error.message.includes("cancelled")) {
        setHasRendered(false);
      }
    }
  };

  // Update effect to use actual PDF dimensions
  useEffect(() => {
    if (viewport && canvasRef.current && rectCanvasRef.current) {
      // Only update rectangle canvas dimensions, not the main canvas
      // The main canvas dimensions are handled in renderPage
      const pdfWidth = viewport.width;
      const pdfHeight = viewport.height;

      // Only update rect canvas since main canvas is handled in renderPage
      rectCanvasRef.current.width = pdfWidth;
      rectCanvasRef.current.height = pdfHeight;

      // Only redraw boxes, don't touch the main canvas
      drawBoxes();
    }
  }, [scale, viewport]);

  // Filter boxes to show only those for current page or all pages
  const visibleBoxes = boxes.filter(
    (box) =>
      (box.pageNumber === currentPage || box.appliesToAllPages) &&
      !box.isConfiguring, // Add this condition to filter out configuring boxes
  );

  // Update drawBoxes to use the shared drawRectangles function
  const drawBoxes = () => {
    if (!rectCanvasRef.current) return;

    const context = rectCanvasRef.current.getContext("2d");
    if (!context) return;

    // Clear the canvas
    context.clearRect(
      0,
      0,
      rectCanvasRef.current.width,
      rectCanvasRef.current.height,
    );

    // Convert boxes to rectangles format (only draw region-based boxes; AI fields have no region)
    const boxesToDraw = visibleBoxes.filter((b) => b.fieldType !== "aiPrompt");
    const rectangles: Rectangle[] = boxesToDraw.map((box) => ({
      id: box.id,
      x: box.x * scale,
      y: box.y * scale,
      width: box.width * scale,
      height: box.height * scale,
      fieldType: box.fieldType === "table" ? "table" : "text",
      pageNumber: box.pageNumber || currentPage,
      columnPositions: box.columnPositions,
      name: box.name, // Add name field
    }));

    // If we're configuring a box, add it to the rectangles array (only if not AI)
    if (configuringBox && configuringBox.fieldType !== "aiPrompt") {
      rectangles.push({
        id: configuringBox.id,
        x: configuringBox.x * scale,
        y: configuringBox.y * scale,
        width: configuringBox.width * scale,
        height: configuringBox.height * scale,
        fieldType: configuringBox.fieldType === "table" ? "table" : "text",
        pageNumber: configuringBox.pageNumber || currentPage,
        columnPositions: configuringBox.columnPositions,
        name: configuringBox.name, // Add name field
      });
    }

    // Use the shared drawRectangles function
    drawRectangles(
      context,
      rectangles,
      selectedBoxId
        ? rectangles.find((r) => r.id === selectedBoxId) || null
        : null,
      scale,
    );

    // Draw column dividers for tables (between columns only; left-edges [0,50,100] → draw at 50)
    rectangles.forEach((box) => {
      if (box.fieldType === "table" && box.columnPositions?.length) {
        const positions = box.columnPositions;
        const dividerPcts = positions[0] === 0 ? positions.slice(1) : positions;
        context.strokeStyle = "red";
        context.lineWidth = 2;
        context.setLineDash([]);
        dividerPcts.forEach((percentage: number) => {
          const pos = box.x + (box.width * percentage) / 100;

          // Draw the vertical line
          context.beginPath();
          context.moveTo(pos, box.y);
          context.lineTo(pos, box.y + box.height);
          context.stroke();

          // Draw triangles at the top and bottom
          const triangleSize = 16; // Increased from 8 to 16
          context.beginPath();
          // Top triangle
          context.moveTo(pos - triangleSize / 2, box.y);
          context.lineTo(pos + triangleSize / 2, box.y);
          context.lineTo(pos, box.y + triangleSize);
          context.closePath();
          context.fillStyle = "red";
          context.fill();

          // Bottom triangle
          context.beginPath();
          context.moveTo(pos - triangleSize / 2, box.y + box.height);
          context.lineTo(pos + triangleSize / 2, box.y + box.height);
          context.lineTo(pos, box.y + box.height - triangleSize);
          context.closePath();
          context.fill();
        });
      }
    });

    // Draw resize handles for selected box
    const selectedBox = visibleBoxes.find((box) => box.id === selectedBoxId);
    if (selectedBox) {
      const handleSize = 8;
      context.fillStyle = "#FF6B6B";
      context.setLineDash([]); // Solid lines for handles

      // Draw corner handles
      context.fillRect(
        selectedBox.x * scale - handleSize / 2,
        selectedBox.y * scale - handleSize / 2,
        handleSize,
        handleSize,
      );
      context.fillRect(
        selectedBox.x * scale + selectedBox.width * scale - handleSize / 2,
        selectedBox.y * scale - handleSize / 2,
        handleSize,
        handleSize,
      );
      context.fillRect(
        selectedBox.x * scale - handleSize / 2,
        selectedBox.y * scale + selectedBox.height * scale - handleSize / 2,
        handleSize,
        handleSize,
      );
      context.fillRect(
        selectedBox.x * scale + selectedBox.width * scale - handleSize / 2,
        selectedBox.y * scale + selectedBox.height * scale - handleSize / 2,
        handleSize,
        handleSize,
      );

      // When extracted data is shown, draw a clear rectangle around the field
      if (
        selectedBox.fieldType !== "aiPrompt" &&
        extractedData &&
        !extractedData.startsWith("Error")
      ) {
        context.strokeStyle = "#0d6efd";
        context.lineWidth = 4;
        context.setLineDash([8, 4]);
        context.strokeRect(
          selectedBox.x * scale,
          selectedBox.y * scale,
          selectedBox.width * scale,
          selectedBox.height * scale,
        );
      }
    }

    // When AI preview has returned backend coords, draw the extracted region rectangle
    if (
      selectedBox?.fieldType === "aiPrompt" &&
      aiPreviewRect &&
      aiPreviewRect.pageNumber === currentPage &&
      viewport &&
      rectCanvasRef.current
    ) {
      const width = aiPreviewRect.x1 - aiPreviewRect.x0;
      const height = aiPreviewRect.y1 - aiPreviewRect.y0;
      const disp = toDisplayCoordinatesImproved(
        aiPreviewRect.x0,
        aiPreviewRect.y0,
        width,
        height,
        viewport,
        rectCanvasRef.current.width,
        rectCanvasRef.current.height,
      );
      context.strokeStyle = "#0d6efd";
      context.lineWidth = 4;
      context.setLineDash([8, 4]);
      context.strokeRect(disp.x, disp.y, disp.width, disp.height);
    }

    // Draw the box being drawn
    if (drawingBoxRef.current) {
      context.strokeStyle = "red";
      context.lineWidth = 2;
      context.setLineDash([]); // Solid line for drawing
      context.strokeRect(
        drawingBoxRef.current.x * scale,
        drawingBoxRef.current.y * scale,
        drawingBoxRef.current.width * scale,
        drawingBoxRef.current.height * scale,
      );
    }
  };

  // Render PDF page
  useEffect(() => {
    // console.log("Render effect triggered with:", {
    //   pdfDoc: !!pdfDoc,
    //   canvas: !!canvasRef.current,
    //   currentPage,
    //   scale,
    // });

    renderPage();

    // Cleanup function to cancel any ongoing render task
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, scale]);

  // Redraw boxes when boxes array or preview data changes (for highlight)
  useEffect(() => {
    if (!canvasRef.current || !viewport) return;

    const context = canvasRef.current.getContext("2d");
    if (!context) return;

    // Draw all boxes
    drawBoxes();
  }, [visibleBoxes, extractedData, selectedBoxId, aiPreviewRect, currentPage]);

  // Add debugging function to print coordinate conversion
  const debugCoordinateConversion = (
    clientX: number,
    clientY: number,
    canvasElement: HTMLCanvasElement,
  ) => {
    const rect = canvasElement.getBoundingClientRect();
    const scaleX = rect.width / canvasElement.width;
    const scaleY = rect.height / canvasElement.height;

    // Display coordinates (what we see on screen)
    const displayX = (clientX - rect.left) / scaleX;
    const displayY = (clientY - rect.top) / scaleY;

    // Backend coordinates using old method (hardcoded US Letter)
    const backendCoordsOld = toBackendCoordinates(
      displayX,
      displayY,
      0, // width not needed for click
      0, // height not needed for click
      viewport,
      canvasElement.width,
      canvasElement.height,
    );

    // Backend coordinates using improved method (actual PDF dimensions)
    const backendCoordsImproved = toBackendCoordinatesImproved(
      displayX,
      displayY,
      0, // width not needed for click
      0, // height not needed for click
      viewport,
      canvasElement.width,
      canvasElement.height,
    );

    // Get backend dimensions info for debugging
    const backendDims = getBackendDimensions(viewport);

    // Calculate click position as percentages of PDF dimensions
    const widthPercentage = (displayX / canvasElement.width) * 100;
    const heightPercentage = (displayY / canvasElement.height) * 100;

    // Calculate backend centering info for debugging
    const backendScaleX = backendDims.width / (viewport?.width || 1);
    const backendScaleY = backendDims.height / (viewport?.height || 1);
    const backendScale = Math.min(backendScaleX, backendScaleY);
    const scaledPdfWidth = (viewport?.width || 0) * backendScale;
    const scaledPdfHeight = (viewport?.height || 0) * backendScale;
    const offsetX = (backendDims.width - scaledPdfWidth) / 2;
    const offsetY = (backendDims.height - scaledPdfHeight) / 2;

    console.log("=== COORDINATE DEBUG ===");
    console.log(
      `🎯 CLICKED AT: ${widthPercentage.toFixed(
        1,
      )}% WIDTH, ${heightPercentage.toFixed(1)}% HEIGHT`,
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━");
    console.log("📍 BACKEND CENTERING CALCULATION:");
    console.log(
      `   PDF size: ${viewport?.width || 0}×${viewport?.height || 0}`,
    );
    console.log(
      `   Backend canvas: ${backendDims.width}×${backendDims.height}`,
    );
    console.log(
      `   Scale factors: X=${backendScaleX.toFixed(
        3,
      )}, Y=${backendScaleY.toFixed(3)}`,
    );
    console.log(`   Backend scale (min): ${backendScale.toFixed(3)}`);
    console.log(
      `   Scaled PDF in backend: ${scaledPdfWidth.toFixed(
        1,
      )}×${scaledPdfHeight.toFixed(1)}`,
    );
    console.log(
      `   Centering offsets: X=${offsetX.toFixed(1)}, Y=${offsetY.toFixed(1)}`,
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━");
    console.log("📍 COORDINATE CONVERSION:");
    console.log(
      `   Browser click: (${displayX.toFixed(1)}, ${displayY.toFixed(1)})`,
    );
    const pdfX = displayX * ((viewport?.width || 1) / canvasElement.width);
    const pdfY = displayY * ((viewport?.height || 1) / canvasElement.height);
    console.log(`   PDF coordinates: (${pdfX.toFixed(1)}, ${pdfY.toFixed(1)})`);
    const scaledX = pdfX * backendScale;
    const scaledY = pdfY * backendScale;
    console.log(`   Scaled: (${scaledX.toFixed(1)}, ${scaledY.toFixed(1)})`);
    console.log(
      `   With centering: (${(scaledX + offsetX).toFixed(1)}, ${(
        scaledY + offsetY
      ).toFixed(1)})`,
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━");
    console.log("📍 PERCENTAGE BREAKDOWN:");
    console.log(
      `   Width: ${widthPercentage.toFixed(
        1,
      )}% (0% = left edge, 50% = center, 100% = right edge)`,
    );
    console.log(
      `   Height: ${heightPercentage.toFixed(
        1,
      )}% (0% = top edge, 50% = center, 100% = bottom edge)`,
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━");
    console.log("PDF orientation:", backendDims.orientation);
    console.log("BACKEND COORDINATES:");
    console.log(
      `   OLD (direct scale): (${backendCoordsOld.x}, ${backendCoordsOld.y})`,
    );
    console.log(
      `   NEW (with centering): (${backendCoordsImproved.x}, ${backendCoordsImproved.y})`,
    );
    console.log("========================");
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    // Debug coordinate conversion on every click
    debugCoordinateConversion(e.clientX, e.clientY, canvasRef.current);

    // console.log({ clientX: e.clientX, clientY: e.clientY });
    // If we're in configuration mode, check for column divider interaction
    if (configStep === "columns" && configuringBox) {
      const rect = canvasRef.current.getBoundingClientRect();
      const scaleX = rect.width / canvasRef.current.width;
      const scaleY = rect.height / canvasRef.current.height;
      const x = (e.clientX - rect.left) / scaleX;
      const y = (e.clientY - rect.top) / scaleY;

      // Check if clicking near a column divider (divider i is between column i and i+1)
      const dividerWidth = 8;
      const positions = configuringBox.columnPositions || [];
      const colCount = configuringBox.columns ?? positions.length;
      const dividerPcts =
        positions.length === colCount ? positions.slice(1) : positions;
      for (let i = 0; i < dividerPcts.length; i++) {
        const dividerX =
          configuringBox.x + (configuringBox.width * dividerPcts[i]) / 100;
        if (Math.abs(x - dividerX) <= dividerWidth / 2) {
          handleColumnDragStart(i);
          return;
        }
      }
      return;
    }

    // If we're in configuration mode, don't allow drawing new rectangles
    if (configStep !== "complete") {
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / canvasRef.current.width;
    const scaleY = rect.height / canvasRef.current.height;
    const x = (e.clientX - rect.left) / scaleX;
    const y = (e.clientY - rect.top) / scaleY;

    // Check if clicking on an existing box
    const clickedBox = visibleBoxes.find((box) => {
      return (
        x >= box.x &&
        x <= box.x + box.width &&
        y >= box.y &&
        y <= box.y + box.height
      );
    });

    if (clickedBox) {
      // Only change selection if clicking on a different box
      if (clickedBox.id !== selectedBoxId) {
        // console.log("Selecting box:", clickedBox.id);
        setSelectedBoxId(clickedBox.id);
        drawBoxes();
      }

      const handleSize = 8; // Back to original size
      const borderSize = 8; // Size for edge detection
      const scaledBorderSize = borderSize / scale; // Adjust border size for scale

      // Check corners first
      if (
        Math.abs(x - clickedBox.x) <= handleSize / scale &&
        Math.abs(y - clickedBox.y) <= handleSize / scale
      ) {
        setResizeHandle("nw");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "nw-resize";
      } else if (
        Math.abs(x - (clickedBox.x + clickedBox.width)) <= handleSize / scale &&
        Math.abs(y - clickedBox.y) <= handleSize / scale
      ) {
        setResizeHandle("ne");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ne-resize";
      } else if (
        Math.abs(x - clickedBox.x) <= handleSize / scale &&
        Math.abs(y - (clickedBox.y + clickedBox.height)) <= handleSize / scale
      ) {
        setResizeHandle("sw");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "sw-resize";
      } else if (
        Math.abs(x - (clickedBox.x + clickedBox.width)) <= handleSize / scale &&
        Math.abs(y - (clickedBox.y + clickedBox.height)) <= handleSize / scale
      ) {
        setResizeHandle("se");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "se-resize";
      }
      // Then check edges
      else if (
        Math.abs(x - clickedBox.x) <= scaledBorderSize &&
        y >= clickedBox.y &&
        y <= clickedBox.y + clickedBox.height
      ) {
        setResizeHandle("w");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ew-resize";
      } else if (
        Math.abs(x - (clickedBox.x + clickedBox.width)) <= scaledBorderSize &&
        y >= clickedBox.y &&
        y <= clickedBox.y + clickedBox.height
      ) {
        setResizeHandle("e");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ew-resize";
      } else if (
        Math.abs(y - clickedBox.y) <= scaledBorderSize &&
        x >= clickedBox.x &&
        x <= clickedBox.x + clickedBox.width
      ) {
        setResizeHandle("n");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ns-resize";
      } else if (
        Math.abs(y - (clickedBox.y + clickedBox.height)) <= scaledBorderSize &&
        x >= clickedBox.x &&
        x <= clickedBox.x + clickedBox.width
      ) {
        setResizeHandle("s");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ns-resize";
      }
      // Then check if inside the box (for moving)
      else if (
        x > clickedBox.x + borderSize &&
        x < clickedBox.x + clickedBox.width - borderSize &&
        y > clickedBox.y + borderSize &&
        y < clickedBox.y + clickedBox.height - borderSize
      ) {
        setIsMoving(true);
        setMoveStartPos({ x, y });
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "grab";
      }
      return;
    }

    // If not clicking on a box, start drawing a new one
    setIsDrawing(true);
    setStartPos({ x, y });
    drawingBoxRef.current = {
      id: Date.now().toString(),
      x,
      y,
      width: 0,
      height: 0,
    };
    canvasRef.current.style.cursor = "crosshair";
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!rectCanvasRef.current) return;

    const rect = rectCanvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / rectCanvasRef.current.width;
    const scaleY = rect.height / rectCanvasRef.current.height;
    const x = (e.clientX - rect.left) / scaleX;
    const y = (e.clientY - rect.top) / scaleY;

    // Handle column dragging
    if (
      configStep === "columns" &&
      isDraggingColumn !== null &&
      configuringBox
    ) {
      handleColumnDrag(e);
      return;
    }

    // Update cursor based on position
    if (!isDrawing && !isMoving && !isResizing) {
      // Check if mouse is near a column divider
      if (configStep === "columns" && configuringBox) {
        const dividerWidth = 8;
        const positions = configuringBox.columnPositions || [];
        const colCount = configuringBox.columns ?? positions.length;
        const dividerPcts =
          positions.length === colCount ? positions.slice(1) : positions;
        for (let i = 0; i < dividerPcts.length; i++) {
          const dividerX =
            configuringBox.x + (configuringBox.width * dividerPcts[i]) / 100;
          if (Math.abs(x - dividerX) <= dividerWidth / 2) {
            rectCanvasRef.current.style.cursor = "ew-resize";
            return;
          }
        }
      }
      rectCanvasRef.current.style.cursor = "default";
    }

    // Handle drawing new box
    if (isDrawing && startPos && drawingBoxRef.current) {
      drawingBoxRef.current.x = Math.min(startPos.x, x);
      drawingBoxRef.current.y = Math.min(startPos.y, y);
      drawingBoxRef.current.width = Math.abs(x - startPos.x);
      drawingBoxRef.current.height = Math.abs(y - startPos.y);
      drawBoxes();
    }
    // Handle moving existing box
    else if (isMoving && moveStartPos && originalBoxPos && selectedBoxId) {
      const dx = x - moveStartPos.x;
      const dy = y - moveStartPos.y;

      setBoxes((prev) =>
        prev.map((box) => {
          if (box.id === selectedBoxId) {
            return {
              ...box,
              x: originalBoxPos.x + dx,
              y: originalBoxPos.y + dy,
            };
          }
          return box;
        }),
      );
      schedulePersistTemplate();
      if (canvasRef.current) {
        canvasRef.current.style.cursor = "grabbing";
      }
      drawBoxes();
    }
    // Handle resizing existing box
    else if (isResizing && originalBoxPos && selectedBoxId) {
      setBoxes((prev) =>
        prev.map((box) => {
          if (box.id === selectedBoxId) {
            let newBox = { ...box };
            switch (resizeHandle) {
              case "nw":
                newBox.x = x;
                newBox.y = y;
                newBox.width = originalBoxPos.x + originalBoxPos.width - x;
                newBox.height = originalBoxPos.y + originalBoxPos.height - y;
                break;
              case "ne":
                newBox.y = y;
                newBox.width = x - originalBoxPos.x;
                newBox.height = originalBoxPos.y + originalBoxPos.height - y;
                break;
              case "sw":
                newBox.x = x;
                newBox.width = originalBoxPos.x + originalBoxPos.width - x;
                newBox.height = y - originalBoxPos.y;
                break;
              case "se":
                newBox.width = x - originalBoxPos.x;
                newBox.height = y - originalBoxPos.y;
                break;
              case "e":
                newBox.width = x - originalBoxPos.x;
                break;
              case "w":
                newBox.x = x;
                newBox.width = originalBoxPos.x + originalBoxPos.width - x;
                break;
              case "n":
                newBox.y = y;
                newBox.height = originalBoxPos.y + originalBoxPos.height - y;
                break;
              case "s":
                newBox.height = y - originalBoxPos.y;
                break;
            }
            return newBox;
          }
          return box;
        }),
      );
      schedulePersistTemplate();
      drawBoxes();
    }
  };

  const handleBoxSelect = (boxId: string) => {
    // console.log("Selecting box from dropdown:", boxId);
    // Only update if selecting a different box
    if (boxId !== selectedBoxId) {
      setSelectedBoxId(boxId);
      // Force redraw when selection changes
      if (canvasRef.current) {
        const context = canvasRef.current.getContext("2d");
        if (context) {
          drawBoxes();
        }
      }
    }
  };

  // Add effect to redraw when selection or box data (e.g. column positions) changes
  useEffect(() => {
    drawBoxes();
  }, [selectedBoxId, boxes, configuringBox]);

  const selectedBox = visibleBoxes.find((box) => box.id === selectedBoxId);

  const startConfiguration = (box: Box) => {
    setConfiguringBox(box);
    setConfigStep("fieldType");

    // Get the canvas and its rendered size
    const canvas = canvasRef.current;
    const canvasRect = canvas?.getBoundingClientRect();
    if (!canvas || !canvasRect) {
      setPopoverPosition({
        x: box.x,
        y: box.y + box.height + POPOVER_GAP,
      });
      return;
    }

    // Calculate scale between canvas internal size and rendered size
    const scaleX = canvasRect.width / canvas.width;
    const scaleY = canvasRect.height / canvas.height;

    // Rectangle position in rendered (screen) coordinates
    const rectLeft = box.x * scaleX;
    const rectTop = box.y * scaleY;
    const rectWidth = box.width * scaleX;

    // Always position popover to the right of the rectangle
    const left = rectLeft + rectWidth + POPOVER_GAP;
    const top = rectTop;

    setPopoverPosition({ x: left, y: top });
  };

  const handleFieldTypeSelect = (type: FieldType) => {
    if (!configuringBox) return;

    // Create a new box with the same coordinates but new field type
    const updatedBox = {
      ...configuringBox,
      fieldType: type,
    };
    setConfiguringBox(updatedBox);

    if (type === "text") {
      // For text fields, move to text field type configuration
      setConfigStep("textFieldType");
    } else {
      // For tables, move to column configuration
      setConfigStep("columns");
      // Left edges per column: 2 cols → [0, 50], 3 cols → [0, 33.33, 66.67]
      const positions = [
        0,
        ...Array.from(
          { length: columnCount - 1 },
          (_, i) => ((i + 1) * 100) / columnCount,
        ),
      ];
      setConfiguringBox({
        ...updatedBox,
        columnPositions: positions,
        columns: columnCount,
        columnConfigs: Array(columnCount)
          .fill(null)
          .map((_, i) => ({
            name: `Column ${i + 1}`,
            type: "text",
            columnWidthPercentage: positions[i],
          })),
      });
    }
  };

  const handleTextFieldTypeSelect = (type: TextFieldType) => {
    if (!configuringBox) return;

    const updatedBox = {
      ...configuringBox,
      textFieldType: type,
    };
    setConfiguringBox(updatedBox);
    setBoxes((prev) =>
      prev.map((box) => (box.id === updatedBox.id ? updatedBox : box)),
    );
    setConfigStep("pageConfig");
    schedulePersistTemplate();
  };

  const handleColumnConfigChange = (
    index: number,
    field: "name" | "type",
    value: string,
  ) => {
    if (!configuringBox) return;

    const newColumnConfigs = [...(configuringBox.columnConfigs || [])];
    newColumnConfigs[index] = {
      ...newColumnConfigs[index],
      [field]: value,
    };

    setConfiguringBox({
      ...configuringBox,
      columnConfigs: newColumnConfigs,
    });
  };

  const handleApplyToAllPagesChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!configuringBox) return;

    const checked = e.target?.checked ?? false;
    setConfiguringBox({
      ...configuringBox,
      appliesToAllPages: checked,
      pageNumber: checked ? undefined : currentPage,
    });
  };

  const handleSelectedBoxApplyToAllPagesChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!selectedBoxId) return;

    const checked = e.target?.checked ?? false;
    setBoxes((prev) =>
      prev.map((box) =>
        box.id === selectedBoxId
          ? {
              ...box,
              appliesToAllPages: checked,
              pageNumber: checked ? undefined : currentPage,
            }
          : box,
      ),
    );
    schedulePersistTemplate();
  };

  const handleColumnCountChange = (count: number) => {
    const validCount = Math.max(2, Math.min(10, Math.floor(count) || 2));
    setColumnCount(validCount);
    if (configuringBox) {
      // Left edges: 2 cols → [0, 50], 3 cols → [0, 33.33, 66.67]
      const positions = [
        0,
        ...Array.from(
          { length: validCount - 1 },
          (_, i) => ((i + 1) * 100) / validCount,
        ),
      ];
      setConfiguringBox({
        ...configuringBox,
        columns: validCount,
        columnPositions: positions,
        columnConfigs: Array(validCount)
          .fill(null)
          .map((_, i) => ({
            name: `Column ${i + 1}`,
            type: "text",
            columnWidthPercentage: positions[i],
          })),
      });
    }
  };

  const handleColumnDragStart = (index: number) => {
    setIsDraggingColumn(index);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = "ew-resize";
    }
  };

  const handleColumnDrag = (e: React.MouseEvent) => {
    if (isDraggingColumn === null || !configuringBox || !canvasRef.current)
      return;

    // Ensure cursor stays as ew-resize during drag
    canvasRef.current.style.cursor = "ew-resize";

    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / canvasRef.current.width;
    const scaleY = rect.height / canvasRef.current.height;

    // Get mouse position in canvas coordinates
    const x = (e.clientX - rect.left) / scaleX;

    // Calculate position relative to the rectangle
    const relativeX = x - configuringBox.x;

    const percentage = (relativeX / configuringBox.width) * 100;

    // Normalize to left-edges: [0, 50, 100] (length = columns)
    let positions = [...(configuringBox.columnPositions || [])];
    const colCount = configuringBox.columns ?? Math.max(positions.length, 2);
    if (positions.length === colCount - 1) {
      positions = [0, ...positions];
    }
    if (positions[0] !== 0) positions[0] = 0;

    // Divider isDraggingColumn is between column isDraggingColumn and isDraggingColumn+1; we update positions[isDraggingColumn+1]
    const prevColumnPos = positions[isDraggingColumn];
    const nextColumnPos = positions[isDraggingColumn + 2] ?? 100;
    const boundedPercentage = Math.max(
      prevColumnPos + 2,
      Math.min(nextColumnPos - 2, percentage),
    );

    positions[isDraggingColumn + 1] = boundedPercentage;

    const updatedBox = {
      ...configuringBox,
      columnPositions: positions,
      columnConfigs: configuringBox.columnConfigs?.map((config, index) => ({
        ...config,
        columnWidthPercentage: positions[index] ?? config.columnWidthPercentage,
      })),
    };
    setConfiguringBox(updatedBox);

    setBoxes((prevBoxes) =>
      prevBoxes.map((box) => (box.id === configuringBox.id ? updatedBox : box)),
    );
    schedulePersistTemplate();
  };

  /** Update column start % from config input; keeps left-edges ordered and redraws. */
  const handleColumnWidthPercentChange = (
    columnIndex: number,
    percent: number,
    isConfiguring: boolean,
  ) => {
    const apply = (box: Box): Box => {
      let positions = [...(box.columnPositions || [])];
      const colCount = box.columns ?? Math.max(positions.length, 2);
      if (positions.length === colCount - 1) positions = [0, ...positions];
      if (positions[0] !== 0) positions[0] = 0;
      const prev = columnIndex <= 0 ? 0 : (positions[columnIndex - 1] ?? 0);
      const next = positions[columnIndex + 1] ?? 100;
      const clamped = Math.max(prev + 1, Math.min(next - 1, percent));
      positions[columnIndex] = clamped;
      return {
        ...box,
        columnPositions: positions,
        columnConfigs:
          box.columnConfigs?.map((c, i) => ({
            ...c,
            columnWidthPercentage: positions[i] ?? c.columnWidthPercentage,
          })) ?? [],
      };
    };
    if (isConfiguring && configuringBox) {
      const updated = apply(configuringBox);
      setConfiguringBox(updated);
      setBoxes((prev) =>
        prev.map((b) => (b.id === configuringBox.id ? updated : b)),
      );
    } else if (selectedBoxId) {
      setBoxes((prev) =>
        prev.map((b) => (b.id === selectedBoxId ? apply(b) : b)),
      );
    }
    schedulePersistTemplate();
  };

  const handleColumnDragEnd = () => {
    setIsDraggingColumn(null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = "default";
    }
    // Restore the popover position after dragging ends
    if (configuringBox) {
      const canvas = canvasRef.current;
      const canvasRect = canvas?.getBoundingClientRect();
      if (canvas && canvasRect) {
        const scaleX = canvasRect.width / canvas.width;
        const scaleY = canvasRect.height / canvas.height;
        const rectLeft = configuringBox.x * scaleX;
        const rectTop = configuringBox.y * scaleY;

        // Position popover to the right of the page
        const left = canvasRect.width - POPOVER_WIDTH - POPOVER_GAP;
        const top = rectTop - POPOVER_HEIGHT - POPOVER_GAP;

        setPopoverPosition({ x: left, y: top });
      }

      // Trigger data extraction with updated column positions
      fetchRectangleData(configuringBox);
    }
  };

  const completeConfiguration = (boxToComplete?: Box) => {
    const sourceBox = boxToComplete || configuringBox;
    if (!sourceBox) return;
    const finalBox = {
      ...sourceBox,
      columns: columnCount,
      columnPositions: sourceBox.columnPositions,
      columnConfigs: sourceBox.columnConfigs?.map((config, index) => ({
        ...config,
        columnWidthPercentage: config.columnWidthPercentage, // Preserve the existing width
      })),
      isConfiguring: false, // Mark as configured
    };
    // console.log("Completing configuration with box:", finalBox);
    setBoxes((prev) =>
      prev.map((box) => (box.id === finalBox.id ? finalBox : box)),
    );
    setConfigStep("complete");
    setConfiguringBox(null);
    fetchRectangleData(finalBox);
    schedulePersistTemplate();
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    // Stop column dragging if active
    if (configStep === "columns" && isDraggingColumn !== null) {
      setIsDraggingColumn(null);
      // Restore popover position
      if (configuringBox) {
        const canvas = canvasRef.current;
        const canvasRect = canvas?.getBoundingClientRect();
        if (canvas && canvasRect) {
          const scaleX = canvasRect.width / canvas.width;
          const scaleY = canvasRect.height / canvas.height;
          const rectLeft = configuringBox.x * scaleX;
          const rectTop = configuringBox.y * scaleY;
          const rectWidth = configuringBox.width * scaleX;

          // Always position popover to the right of the rectangle
          const left = rectLeft + rectWidth + POPOVER_GAP;
          const top = rectTop;

          setPopoverPosition({ x: left, y: top });
        }
      }
      return;
    }

    if (isDrawing && startPos && drawingBoxRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const scaleX = rect.width / canvasRef.current.width;
      const scaleY = rect.height / canvasRef.current.height;
      const x = (e.clientX - rect.left) / scaleX;
      const y = (e.clientY - rect.top) / scaleY;

      if (x !== startPos.x && y !== startPos.y) {
        // Get the next field number
        const nextFieldNumber = boxes.length + 1;
        const newBox = {
          id: drawingBoxRef.current.id,
          x: Math.min(startPos.x, x),
          y: Math.min(startPos.y, y),
          width: Math.abs(x - startPos.x),
          height: Math.abs(y - startPos.y),
          pageNumber: currentPage,
          appliesToAllPages: false,
          name: `Field ${nextFieldNumber}`, // Add sequential name
          isConfiguring: true, // Mark as configuring
        };

        setBoxes((prev) => [...prev, newBox]);
        setSelectedBoxId(newBox.id);
        startConfiguration(newBox);
        schedulePersistTemplate();
      }
    }

    setIsDrawing(false);
    setIsMoving(false);
    setIsResizing(false);
    setResizeHandle(null);
    setMoveStartPos(null);
    setOriginalBoxPos(null);
    setStartPos(null);
    drawingBoxRef.current = null;
    canvasRef.current.style.cursor = "crosshair";

    if (!isDrawing) {
      const selectedBox = boxes.find((box) => box.id === selectedBoxId);
      fetchRectangleData(selectedBox); // when moving rectangle around
    }
  };

  const handleAddAiField = (params: {
    name: string;
    prompt: string;
    dataType: "text" | "table";
    appliesToAllPages: boolean;
  }) => {
    const id = Date.now().toString();
    const newBox: Box = {
      id,
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      fieldType: "aiPrompt",
      name: params.name.trim(),
      aiPrompt: { prompt: params.prompt.trim() },
      textFieldType: params.dataType === "text" ? "text" : undefined,
      appliesToAllPages: params.appliesToAllPages,
      pageNumber: params.appliesToAllPages ? undefined : currentPage,
      ...(params.dataType === "table"
        ? {
            columns: 2,
            columnPositions: [0, 50],
            columnConfigs: [
              { name: "Column 1", type: "text", columnWidthPercentage: 0 },
              { name: "Column 2", type: "text", columnWidthPercentage: 50 },
            ],
          }
        : {}),
    };
    setBoxes((prev) => [...prev, newBox]);
    setSelectedBoxId(id);
    lastCreatedAiFieldIdRef.current = id;
    setShowAddAiModal(false);
    schedulePersistTemplate();
  };

  const handleDeleteBox = () => {
    if (selectedBoxId) {
      setBoxes((prev) => {
        const newBoxes = prev.filter((box) => box.id !== selectedBoxId);
        // Select the last box if there are any boxes left
        if (newBoxes.length > 0) {
          setSelectedBoxId(newBoxes[newBoxes.length - 1].id);
        } else {
          setSelectedBoxId(null);
        }
        return newBoxes;
      });
      schedulePersistTemplate();
    }
  };

  // Build template payload for API: { name, template: JSON.stringify({ rectangles }), version: "v2" }
  const buildTemplatePayload = React.useCallback(() => {
    const templateName = template?.name?.trim() || "";
    const templateFields = boxes.map((box) => {
      if (box.fieldType === "aiPrompt" && box.aiPrompt) {
        const isAiTable =
          (box.columns ?? 0) > 0 || (box.columnConfigs?.length ?? 0) > 0;
        return {
          id: box.id,
          name: box.name || `Field ${box.id.slice(-4)}`,
          locationType: "aiPrompt" as const,
          toolType: isAiTable ? ("tables" as const) : ("ocr" as const),
          fieldType: (isAiTable ? "text" : box.textFieldType || "text") as
            | "text"
            | "number"
            | "date"
            | "currency",
          fixedLocation: null,
          variableLocation: null,
          aiPrompt: { prompt: box.aiPrompt.prompt },
          pages: {
            page: box.appliesToAllPages ? 0 : (box.pageNumber || 1) - 1,
            pagesType: box.appliesToAllPages ? "allPages" : "currentPage",
            include: [],
            exclude: [],
          },
          tableConfig:
            box.columns && box.columns > 0
              ? {
                  columns: Array.from({ length: box.columns }, (_, index) => ({
                    name:
                      box.columnConfigs?.[index]?.name || `Column ${index + 1}`,
                    index,
                    fieldType: box.columnConfigs?.[index]?.type || "text",
                    columnWidthPercentage:
                      (box.columnPositions || [])[index - 1] ??
                      (index * 100) / (box.columns || 1),
                    removeFromOutput: false,
                  })),
                  headerRows: 0,
                  transforms: {
                    verticalMergeMasks: null,
                    removeTopRows: null,
                    removeBottomRows: null,
                  },
                }
              : {
                  columns: [],
                  headerRows: 0,
                  transforms: {
                    verticalMergeMasks: null,
                    removeTopRows: null,
                    removeBottomRows: null,
                  },
                },
          checkLabels: { checked: "yes", unchecked: "no" },
        };
      }

      const backendCoords = toBackendCoordinatesImproved(
        box.x,
        box.y,
        box.width,
        box.height,
        viewport,
        canvasRef.current?.width || 0,
        canvasRef.current?.height || 0,
      );

      return {
        id: box.id,
        name: box.name || `Field ${box.id.slice(-4)}`,
        fieldType: box.fieldType === "table" ? "text" : "text",
        toolType: box.fieldType === "table" ? "tables" : "ocr",
        locationType: "fixed",
        fixedLocation: {
          pageLocation: {
            x0: backendCoords.x,
            y0: backendCoords.y,
            x1: backendCoords.x + backendCoords.width,
            y1: backendCoords.y + backendCoords.height,
          },
        },
        variableLocation: {
          variableLocations: [],
        },
        pages: {
          page: (box.pageNumber || 1) - 1 || 0,
          pagesType: box.appliesToAllPages ? "allPages" : "currentPage",
          include: [],
          exclude: [],
        },
        tableConfig:
          box.fieldType === "table"
            ? {
                columns: Array.from(
                  { length: box.columns || 0 },
                  (_, index) => {
                    const positions = box.columnPositions || [];
                    const colCount = box.columns || 0;
                    // columnPositions can be stored as left-edges [0, 50, ...] (length === columns) or dividers [50] (length === columns-1)
                    const pos =
                      index === 0
                        ? 0
                        : positions.length === colCount
                          ? (positions[index] ?? 0)
                          : (positions[index - 1] ?? 0);
                    const pct =
                      index === 0
                        ? 0
                        : pos ||
                          box.columnConfigs?.[index]?.columnWidthPercentage ||
                          0;
                    return {
                      name:
                        box.columnConfigs?.[index]?.name ||
                        `Column ${index + 1}`,
                      index: index,
                      fieldType: box.columnConfigs?.[index]?.type || "text",
                      columnWidthPercentage: pct,
                      removeFromOutput: false,
                    };
                  },
                ),
                headerRows: 0,
                transforms: {
                  verticalMergeMasks: null,
                  removeTopRows: null,
                  removeBottomRows: null,
                },
              }
            : {
                columns: [],
                headerRows: 0,
                transforms: {
                  verticalMergeMasks: null,
                  removeTopRows: null,
                  removeBottomRows: null,
                },
              },
        checkLabels: {
          checked: "yes",
          unchecked: "no",
        },
      };
    });
    return {
      name: templateName,
      template: JSON.stringify({ rectangles: templateFields }),
      version: "v2",
      templateFields,
    };
  }, [boxes, template?.name, viewport]);

  const handleSaveTemplate = async (createJob: boolean = false) => {
    const query = queryString.parse(location.search);
    const templateNameFromQuery = query.templateName as string;
    if (!templateId && !templateNameFromQuery && !template?.name?.trim()) {
      dispatch(
        createAlert({
          type: "error",
          message: "Template name is required",
          timeout: 5000,
        }),
      );
      return;
    }
    if (!templateId && !isTemplateNameValid) {
      dispatch(
        createAlert({
          type: "error",
          message: "Template name already exists, choose a different name",
          timeout: 5000,
        }),
      );
      return;
    }

    const { templateFields } = buildTemplatePayload();

    // When editing a template there's no document to process — only save, never create a job
    const isEditing = templateId != null;
    const shouldCreateJob = createJob && !isEditing;

    try {
      const templateData = {
        name: templateNameFromQuery || template?.name || "",
        metadataFields: [],
        logoURL: "",
        intuitNumber: "",
        visibility: "private",
        category: "",
        type: "FreeForm",
        description: "",
        version: "v2",
        template: JSON.stringify({
          rectangles: templateFields,
        }),
        templateDocumentId: documentRow ? documentRow.id : documentId,
        createJob: shouldCreateJob,
        l1,
        l2,
      };

      const newTemplate = templateId
        ? await updateTemplate(templateId, templateData)
        : await createTemplate(templateData);

      const fullMessage = shouldCreateJob
        ? "Template saved and data extraction started"
        : "Template saved";

      dispatch(
        createAlert({
          type: "success",
          message: fullMessage,
          timeout: 5000,
        }),
      );

      if (shouldCreateJob && newTemplate.job && newTemplate.job.id) {
        const queryParams = new URLSearchParams(history.location.search);
        queryParams.append("jobId", newTemplate.job.id);
        queryParams.append("jobType", "template");
        queryParams.append(
          "newDocuments",
          documentRow ? documentRow.id.toString() : documentId,
        );
        history.push(`/jobData?${queryParams.toString()}`);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      if (errorMessage.includes("Validation error")) {
        setIsTemplateNameValid(false);
      }
      dispatch(
        createAlert({
          type: "error",
          message: errorMessage.includes("Validation error")
            ? "Template name already exists"
            : "Error saving template",
          timeout: 5000,
        }),
      );
    }
  };

  const runPendingExtraction = () => {
    if (pendingExtractionAction === "saveAndStart") {
      setShowExtractionWarningModal(false);
      setPendingExtractionAction(null);
      handleSaveTemplate(true);
    } else if (pendingExtractionAction === "extractFromDocument") {
      setShowExtractionWarningModal(false);
      setPendingExtractionAction(null);
      doExtractFromCurrentDocument();
    }
  };

  const handleExtractFromCurrentDocument = () => {
    setPendingExtractionAction("extractFromDocument");
    setShowExtractionWarningModal(true);
  };

  const doExtractFromCurrentDocument = () => {
    if (!templateId || !templateDocumentId) return;
    const docId = parseInt(templateDocumentId, 10);
    if (Number.isNaN(docId)) return;
    setExtractFromCurrentLoading(true);
    createJob({
      templateId,
      documents: [docId],
      documentsToSplit: [],
      documentPages: {},
      enableMultiPage: false,
      jobName: "",
      selectedTemplateFields: {},
      isGeneric: false,
      enableBankMode: false,
      forceOcr: false,
      disableAutoRotation: false,
      tags: [],
      l1: l1 || "",
      l2: l2 || "",
    })
      .then((rsp: any) => {
        setExtractFromCurrentLoading(false);
        dispatch(setJobData({ job: rsp, progress: null }));
        dispatch(setJobDocumentData([]));
        const queryParams = new URLSearchParams();
        queryParams.append("jobId", rsp.id);
        queryParams.append("jobType", "template");
        if (l1 && l2) {
          queryParams.append("l1", l1);
          queryParams.append("l2", l2);
          queryParams.append("newDocuments", templateDocumentId);
        }
        history.push(`/jobData?${queryParams.toString()}`);
        dispatch(
          createAlert({
            type: "success",
            message: "Extraction started",
            timeout: 5000,
          }),
        );
      })
      .catch((err) => {
        setExtractFromCurrentLoading(false);
        dispatch(
          createAlert({
            type: "error",
            message: err?.message || "Error starting extraction",
            timeout: 5000,
          }),
        );
      });
  };

  const handleAddDocumentsToExtract = () => {
    if (!templateId) return;
    const params = new URLSearchParams();
    params.set("templateId", String(templateId));
    if (template?.name) params.set("template", template.name);
    if (l1) params.set("l1", l1);
    if (l2) params.set("l2", l2);
    history.push(`/extract/template?${params.toString()}`);
  };

  // Keep refs updated so schedulePersistTemplate (called from user actions only) can read current payload.
  useEffect(() => {
    templateIdPersistRef.current = templateId ?? null;
    getPayloadPersistRef.current = () => {
      const p = buildTemplatePayload();
      return { name: p.name, template: p.template, version: p.version };
    };
  }, [templateId, buildTemplatePayload]);

  /** Schedules a single debounced PATCH of the template. Call only when the user adds, removes, or edits a field (or template name). Never call on load. */
  const schedulePersistTemplate = React.useCallback(() => {
    if (persistTemplateTimeoutRef.current) {
      clearTimeout(persistTemplateTimeoutRef.current);
      persistTemplateTimeoutRef.current = null;
    }
    const tid = templateIdPersistRef.current;
    if (!tid) return;
    persistTemplateTimeoutRef.current = setTimeout(() => {
      persistTemplateTimeoutRef.current = null;
      const payload = getPayloadPersistRef.current();
      updateTemplate(tid, {
        name: payload.name,
        template: payload.template,
        version: payload.version,
      }).catch(() => {});
    }, 500);
  }, []);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedBoxId || !e.target) return;
    const newName = e.target.value;
    setBoxes((prev) =>
      prev.map((box) =>
        box.id === selectedBoxId ? { ...box, name: newName } : box,
      ),
    );
    schedulePersistTemplate();
  };

  const fetchRectangleData = async (box?: Box) => {
    if (!box || !effectiveDocumentId || box.fieldType === "aiPrompt") {
      return;
    }

    try {
      setIsLoadingData(true);
      setExtractedData(null);

      const { x, y, width, height } = box;
      const x0 = x;
      const x1 = x + width;
      const y0 = y;
      const y1 = y + height;

      let data;
      if (box.fieldType === "text") {
        const x0 = box.x;
        const y0 = box.y;
        const x1 = box.x + box.width;
        const y1 = box.y + box.height;

        if (!viewport || !canvasRef.current) {
          console.error("Viewport or canvas not available");
          return;
        }

        const backendCoords = toBackendCoordinatesImproved(
          x0,
          y0,
          box.width,
          box.height,
          viewport,
          canvasRef.current?.width || 0,
          canvasRef.current?.height || 0,
        );

        data = await getFragment({
          checkLabels: { checked: "true", unchecked: "false" },
          fieldType: "text",
          fragmentId: null,
          jobId: null,
          documentId: parseInt(effectiveDocumentId, 10),
          pageNumber: (box.pageNumber || currentPage) - 1,
          toolType: "ocr",
          x0: backendCoords.x,
          x1: backendCoords.x + backendCoords.width,
          y0: backendCoords.y,
          y1: backendCoords.y + backendCoords.height,
        });
      } else if (box.fieldType === "table") {
        const x0 = box.x;
        const y0 = box.y;
        const x1 = box.x + box.width;
        const y1 = box.y + box.height;

        if (!viewport || !canvasRef.current) {
          console.error("Viewport or canvas not available");
          return;
        }

        const backendCoords = toBackendCoordinatesImproved(
          x0,
          y0,
          box.width,
          box.height,
          viewport,
          canvasRef.current?.width || 0,
          canvasRef.current?.height || 0,
        );

        data = await getTable({
          fragmentId: null,
          jobId: null,
          documentId: parseInt(effectiveDocumentId, 10),
          pageNumber: (box.pageNumber || currentPage) - 1,
          toolType: "tables",
          x0: backendCoords.x,
          x1: backendCoords.x + backendCoords.width,
          y0: backendCoords.y,
          y1: backendCoords.y + backendCoords.height,
          tableConfig: {
            columns:
              box.columnConfigs?.map((col, index) => ({
                name: col.name,
                type: col.type,
                index,
                fieldType: "text",
                columnWidthPercentage: col.columnWidthPercentage as any,
                line: {
                  id: `line-${index}`,
                  horizontalLine: null,
                  stroke: "#000",
                  strokeWidth: 1,
                  verticalLine: null,
                  type: "vertical",
                  draggable: false,
                  text: "",
                  dash: null,
                },
              })) || [],
            headerRows: 1,
            transforms: {
              verticalMergeMasksEnabled: false,
              alignMiddleEnabled: false,
              verticalMergeMasks: null,
              alignMiddle: null,
            },
          },
        });
      }

      setExtractedData(JSON.stringify(data, null, 2));
    } catch (error) {
      console.error("Error fetching rectangle data:", error);
      setExtractedData("Error fetching data. Please try again.");
    } finally {
      setIsLoadingData(false);
    }
  };

  const extractRectFromFragmentResponse = (
    raw: Record<string, unknown> | null,
    pageNumber0Based: number,
  ): {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    pageNumber: number;
  } | null => {
    if (!raw || typeof raw !== "object") return null;
    const x0 = Number(raw.x0);
    const y0 = Number(raw.y0);
    const x1 = Number(raw.x1);
    const y1 = Number(raw.y1);
    if (
      Number.isNaN(x0) ||
      Number.isNaN(y0) ||
      Number.isNaN(x1) ||
      Number.isNaN(y1) ||
      x1 <= x0 ||
      y1 <= y0
    ) {
      return null;
    }
    return { x0, y0, x1, y1, pageNumber: pageNumber0Based + 1 };
  };

  const fetchAiPromptPreview = async (box: Box | undefined) => {
    if (
      !box ||
      box.fieldType !== "aiPrompt" ||
      !effectiveDocumentId ||
      !box.aiPrompt?.prompt?.trim()
    ) {
      setAiPreviewText(null);
      setAiPreviewError(null);
      setAiPreviewRect(null);
      return;
    }
    const prompt = box.aiPrompt.prompt.trim();
    const pageNum = box.appliesToAllPages
      ? currentPage
      : (box.pageNumber ?? currentPage);
    const docId = parseInt(effectiveDocumentId, 10);
    const pageNumber = pageNum - 1;
    try {
      setAiPreviewError(null);
      setAiPreviewLoading(true);
      const isTable =
        (box.columns ?? 0) > 0 || (box.columnConfigs?.length ?? 0) > 0;
      if (isTable) {
        const tableConfig = {
          columns:
            box.columnConfigs?.map((col, index) => ({
              name: col.name,
              type: col.type,
              index,
              fieldType: "text" as const,
              columnWidthPercentage: col.columnWidthPercentage ?? 0,
              line: {
                id: `line-${index}`,
                horizontalLine: null,
                stroke: "#000",
                strokeWidth: 1,
                verticalLine: null,
                type: "vertical" as const,
                draggable: false,
                text: "",
                dash: null,
              },
            })) ?? [],
          headerRows: 1,
          transforms: {
            verticalMergeMasksEnabled: false,
            alignMiddleEnabled: false,
            verticalMergeMasks: null,
            alignMiddle: null,
          },
        };
        const data = await getTable({
          documentId: docId,
          pageNumber,
          fragmentId: null,
          jobId: null,
          toolType: "aiPrompt",
          x0: 0,
          y0: 0,
          x1: 1,
          y1: 1,
          tableConfig,
          prompt,
        });
        const raw = data as Record<string, unknown>;
        const text =
          raw?.extractedText ??
          raw?.improvedText ??
          (typeof data === "string" ? data : JSON.stringify(data, null, 2));
        setAiPreviewText(text != null ? String(text) : null);
        setAiPreviewRect(extractRectFromFragmentResponse(raw, pageNumber));
      } else {
        const data = await getFragment({
          documentId: docId,
          pageNumber,
          x0: 0,
          y0: 0,
          x1: 1,
          y1: 1,
          toolType: "aiPrompt",
          jobId: null,
          fieldType: "text",
          fragmentId: null,
          checkLabels: { checked: "true", unchecked: "false" },
          prompt,
        });
        const raw = data as Record<string, unknown>;
        const text = raw?.extractedText ?? raw?.improvedText ?? "";
        setAiPreviewText(text != null ? String(text) : null);
        setAiPreviewRect(extractRectFromFragmentResponse(raw, pageNumber));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load preview";
      setAiPreviewError(msg);
      setAiPreviewText(null);
      setAiPreviewRect(null);
    } finally {
      setAiPreviewLoading(false);
    }
  };

  // When switching fields from the dropdown: clear previous preview and run preview for the selected field. Do not run on initial load.
  useEffect(() => {
    const box = visibleBoxes.find((b) => b.id === selectedBoxId);
    const prevId = prevSelectedBoxIdRef.current;
    const selectionChanged = prevId !== selectedBoxId;
    prevSelectedBoxIdRef.current = selectedBoxId;

    if (selectionChanged) {
      setAiPreviewText(null);
      setAiPreviewError(null);
      setAiPreviewRect(null);
    }

    if (isInitialFieldLoadRef.current) {
      isInitialFieldLoadRef.current = false;
      return;
    }
    if (!selectionChanged || !box || !effectiveDocumentId) return;

    if (box.fieldType === "aiPrompt") {
      if (box.aiPrompt?.prompt?.trim()) {
        lastCreatedAiFieldIdRef.current = null;
        fetchAiPromptPreview(box);
      }
    } else {
      if (viewport) fetchRectangleData(box);
    }
  }, [selectedBoxId, visibleBoxes, viewport]);

  // Update the condition for showing the "Select data to extract" message
  const isPageRendered = hasRendered && pdfDoc !== null;

  // Add keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Delete") {
        if (configuringBox) {
          // Cancel configuration
          setConfiguringBox(null);
          setConfigStep("complete");
        } else if (selectedBoxId) {
          // Delete selected box
          handleDeleteBox();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [configuringBox, selectedBoxId]);

  const selectedBoxForBanner = boxes.find((b) => b.id === selectedBoxId);
  const previewPage = selectedBoxForBanner?.pageNumber ?? currentPage;
  const previewFieldName =
    selectedBoxForBanner?.name ||
    (selectedBoxForBanner ? `Field ${selectedBoxForBanner.id.slice(-4)}` : "");
  const isPreviewLoading = isLoadingData || aiPreviewLoading;
  const hasPreviewData =
    selectedBoxForBanner &&
    (extractedData ||
      (selectedBoxForBanner.fieldType === "aiPrompt" && aiPreviewText != null));
  const showPreviewBanner = isPreviewLoading || hasPreviewData;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        padding: "20px",
        position: "relative",
        height: "85vh",
        width: "100%",
      }}
    >
      {/* Top preview banner - very visible when previewing */}
      {showPreviewBanner && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
            background: isPreviewLoading ? "#fff3cd" : "#e7f3ff",
            color: isPreviewLoading ? "#856404" : "#0c5460",
            border: `1px solid ${isPreviewLoading ? "#ffc107" : "#b8daff"}`,
          }}
          role="status"
          aria-live="polite"
        >
          {isPreviewLoading ? (
            <>
              <ClipLoader
                size={18}
                color={isPreviewLoading ? "#856404" : "#0c5460"}
              />
              <span>
                Loading preview for &quot;{previewFieldName || "field"}&quot; on
                page {previewPage}…
              </span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon="eye" style={{ fontSize: "16px" }} />
              <span>
                Preview field: &quot;{previewFieldName}&quot; on page{" "}
                {previewPage} (
                {selectedBoxForBanner?.appliesToAllPages
                  ? "any page"
                  : "current page only"}
                ).
              </span>
            </>
          )}
        </div>
      )}

      {/* Top bar: page nav (left) + Add field, Save/Extract, sidebar toggle (right) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "10px 14px",
          background: "#f5f5f5",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          flexWrap: "wrap",
        }}
      >
        {/* Left: template name + page navigation */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            value={template?.name || ""}
            onChange={(e) => {
              const value = e.target.value;
              dispatch(
                templateSetData({
                  template: { ...template, name: value },
                }),
              );
              setIsTemplateNameValid(true);
              schedulePersistTemplate();
              if (templateNameCheckTimeoutRef.current) {
                clearTimeout(templateNameCheckTimeoutRef.current);
                templateNameCheckTimeoutRef.current = null;
              }
              if (value.trim()) {
                templateNameCheckTimeoutRef.current = setTimeout(() => {
                  templateNameCheckTimeoutRef.current = null;
                  getTemplateByName(value)
                    .then((list) => {
                      const arr = Array.isArray(list) ? list : [];
                      setIsTemplateNameValid(arr.length === 0);
                    })
                    .catch(() => setIsTemplateNameValid(true));
                }, 400);
              }
            }}
            onBlur={() => {
              if (templateId && template?.name?.trim()) {
                const payload = buildTemplatePayload();
                updateTemplate(templateId, {
                  name: payload.name,
                  template: payload.template,
                  version: payload.version,
                }).catch(() => {});
              }
            }}
            placeholder="Template name"
            title={!isTemplateNameValid ? "Name already exists" : ""}
            style={{
              width: "140px",
              minWidth: "100px",
              padding: "6px 8px",
              borderRadius: "4px",
              border: isTemplateNameValid
                ? "1px solid #ccc"
                : "1px solid #ff4444",
              fontSize: "13px",
              backgroundColor: isTemplateNameValid ? "white" : "#fff8f8",
            }}
          />
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            style={{
              padding: "6px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "13px",
            }}
          >
            <span>←</span> Previous
          </button>
          <span style={{ padding: "0 10px", fontSize: "13px" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            style={{
              padding: "6px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "13px",
            }}
          >
            Next <span>→</span>
          </button>
        </div>

        {/* Right: Add field, Save/Extract, sidebar toggle */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setAiFieldForm({
                name: "",
                prompt: "",
                dataType: "text",
                appliesToAllPages: false,
              });
              setShowAddAiModal(true);
            }}
            title="Add a field—describe what you want in words"
            className="d-flex align-items-center"
          >
            <FontAwesomeIcon icon="magic" className="mr-1" />
            <span>Add field</span>
          </Button>

          {selectedBoxId &&
            (() => {
              const box = boxes.find((b) => b.id === selectedBoxId);
              if (!box) return null;
              if (box.fieldType === "aiPrompt") {
                return (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!box.aiPrompt?.prompt?.trim() || aiPreviewLoading}
                    onClick={() => fetchAiPromptPreview(box)}
                    className="d-flex align-items-center"
                    title="Preview extraction for this field"
                  >
                    {aiPreviewLoading ? (
                      <ClipLoader size={14} />
                    ) : (
                      <FontAwesomeIcon icon="eye" className="mr-1" />
                    )}
                    <span>
                      {aiPreviewLoading ? "Loading…" : "Preview field"}
                    </span>
                  </Button>
                );
              }
              return (
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={isLoadingData}
                  onClick={() => fetchRectangleData(box)}
                  className="d-flex align-items-center"
                  title="Extract data from this field on the current page"
                >
                  {isLoadingData ? (
                    <ClipLoader size={14} />
                  ) : (
                    <FontAwesomeIcon icon="search" className="mr-1" />
                  )}
                  <span>{isLoadingData ? "Loading…" : "Extract data"}</span>
                </Button>
              );
            })()}

          {templateId ? (
            <>
              <Button
                variant="primary"
                size="sm"
                disabled={
                  !templateDocumentId ||
                  extractFromCurrentLoading ||
                  boxes.length === 0
                }
                onClick={handleExtractFromCurrentDocument}
                className="d-flex align-items-center"
                title="Run this template on the current document"
              >
                {extractFromCurrentLoading ? (
                  <>
                    <ClipLoader size={14} color="#fff" />
                    <span className="ml-1">Starting…</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="play" className="mr-1" />
                    <span>Extract data from this document</span>
                  </>
                )}
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddDocumentsToExtract}
                style={{ borderWidth: "1.5px", fontWeight: 500 }}
                title="Choose more documents to run this template on"
              >
                <FontAwesomeIcon icon="folder-open" className="mr-1" />
                Extract data from more documents
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              disabled={
                !(template?.name || "").trim() ||
                !isTemplateNameValid ||
                boxes.length === 0
              }
              onClick={() => {
                setPendingExtractionAction("saveAndStart");
                setShowExtractionWarningModal(true);
              }}
              className="d-flex align-items-center"
            >
              <FontAwesomeIcon icon="play" className="mr-1" />
              <span>Start extraction</span>
            </Button>
          )}

          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{
              width: "36px",
              height: "36px",
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: "6px",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={
              sidebarCollapsed ? "Open fields panel" : "Close fields panel"
            }
          >
            <FontAwesomeIcon
              icon={sidebarCollapsed ? "chevron-left" : "chevron-right"}
            />
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "20px", flex: 1, minHeight: 0 }}>
        {/* Page thumbnails sidebar */}
        <div
          style={{
            width: "120px",
            padding: "10px",
            background: "#f5f5f5",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            overflowY: "auto",
            maxHeight: "100%",
            flexShrink: 0,
          }}
        >
          {pageThumbnails.map(({ pageNumber, thumbnail }) => (
            <div
              key={pageNumber}
              style={{
                cursor: "pointer",
                border:
                  pageNumber === currentPage
                    ? "2px solid #FF6B6B"
                    : "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px",
                background: "white",
              }}
              onClick={() => setCurrentPage(pageNumber)}
            >
              <img
                src={thumbnail}
                alt={`Page ${pageNumber}`}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
              <div
                style={{
                  textAlign: "center",
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                Page {pageNumber}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            flex: "1 1 auto",
            position: "relative",
            minWidth: 0,
            height: "100%",
          }}
        >
          <div style={{ position: "relative", height: "100%" }}>
            <canvas
              ref={canvasRef}
              style={{
                display: "block",
                margin: "0 auto",
                border: "2px solid #000000", // Thick red border for debugging
                background: "#fafafa",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 1,
              }}
            />
            <canvas
              ref={rectCanvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              style={{
                display: "block",
                margin: "0 auto",
                position: "absolute",
                top: 0,
                left: 0,
                cursor: "crosshair",
                zIndex: 2,
              }}
            />
            {boxes.length === 0 &&
              showAddFieldChoiceOverlay &&
              !isLoadingData &&
              isPageRendered && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(255, 255, 255, 0.98)",
                    padding: "24px 32px",
                    borderRadius: "12px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                    textAlign: "center",
                    pointerEvents: "auto",
                    zIndex: 1000,
                    border: "1px solid #e0e0e0",
                    maxWidth: "440px",
                  }}
                >
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "18px" }}>
                    Add a field
                  </h3>
                  <p
                    style={{
                      margin: "0 0 20px 0",
                      fontSize: "14px",
                      color: "#555",
                    }}
                  >
                    Pick one:
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        padding: "14px 16px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        textAlign: "left",
                        background: "#fafafa",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: "6px",
                          fontSize: "14px",
                        }}
                      >
                        1. Describe in plain English
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#666",
                          marginBottom: "10px",
                        }}
                      >
                        Tell us in words what data to extract (e.g. &quot;the
                        total amount due&quot; or &quot;all line items&quot;).
                        We&apos;ll find it in the document.
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowAddAiModal(true)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FontAwesomeIcon icon="magic" />
                        Add field
                      </Button>
                    </div>
                    <div
                      style={{
                        padding: "14px 16px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        textAlign: "left",
                        background: "#fafafa",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: "6px",
                          fontSize: "14px",
                        }}
                      >
                        2. Fixed location — draw a box
                      </div>
                      <div style={{ fontSize: "13px", color: "#666" }}>
                        The data always appears in the same place (e.g. a form
                        field or table). Click and drag on the document to draw
                        a box.
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddFieldChoiceOverlay(false)}
                        style={{
                          marginTop: "10px",
                          padding: 0,
                          border: "none",
                          background: "none",
                          color: "#0066cc",
                          fontSize: "13px",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        Draw on document
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Configuration Popover */}
        {configStep !== "complete" && configuringBox && popoverPosition && (
          <div
            style={{
              position: "absolute",
              left: popoverPosition.x,
              top: popoverPosition.y,
              width: "300px",
              padding: "20px",
              background: "white",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1001,
              pointerEvents: "auto", // Ensure the popover is interactive
            }}
          >
            {/* <h3 style={{ margin: "0 0 15px 0" }}>Configure Rectangle</h3> */}
            <div
              style={{
                marginBottom: "15px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  padding: "4px 8px",
                  background:
                    configStep === "fieldType" ? "#4CAF50" : "#e0e0e0",
                  color: configStep === "fieldType" ? "white" : "black",
                  borderRadius: "12px",
                  fontSize: "12px",
                }}
              >
                1
              </div>
              <div
                style={{
                  padding: "4px 8px",
                  background:
                    configStep === "textFieldType" ||
                    configStep === "columns" ||
                    configStep === "columnConfig"
                      ? "#4CAF50"
                      : "#e0e0e0",
                  color:
                    configStep === "textFieldType" ||
                    configStep === "columns" ||
                    configStep === "columnConfig"
                      ? "white"
                      : "black",
                  borderRadius: "12px",
                  fontSize: "12px",
                }}
              >
                2
              </div>
              <div
                style={{
                  padding: "4px 8px",
                  background:
                    configStep === "pageConfig" ? "#4CAF50" : "#e0e0e0",
                  color: configStep === "pageConfig" ? "white" : "black",
                  borderRadius: "12px",
                  fontSize: "12px",
                }}
              >
                3
              </div>
            </div>

            {configStep === "pageConfig" && (
              <div>
                <h4 style={{ margin: "0 0 10px 0" }}>Page Configuration</h4>
                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <button
                      onClick={() => {
                        const updatedBox = {
                          ...configuringBox,
                          appliesToAllPages: false,
                          pageNumber: currentPage,
                        };
                        setConfiguringBox(updatedBox);
                        setBoxes((prev) =>
                          prev.map((box) =>
                            box.id === selectedBoxId ? updatedBox : box,
                          ),
                        );
                        completeConfiguration(updatedBox);
                      }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        padding: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        cursor: "pointer",
                        background: "white",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ fontWeight: "500" }}>Current Page Only</div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#666",
                        }}
                      >
                        This field will only appear on page {currentPage}. Use
                        this for fields that are unique to this page.
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        const updatedBox = {
                          ...configuringBox,
                          appliesToAllPages: true,
                          pageNumber: undefined,
                        };
                        setConfiguringBox(updatedBox);
                        setBoxes((prev) =>
                          prev.map((box) =>
                            box.id === selectedBoxId ? updatedBox : box,
                          ),
                        );
                        completeConfiguration(updatedBox);
                      }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        padding: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        cursor: "pointer",
                        background: "white",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ fontWeight: "500" }}>Any page</div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#666",
                        }}
                      >
                        This field will appear on every page of the document.
                        Use this for fields that repeat across pages.
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {configStep === "fieldType" && (
              <div>
                <h4 style={{ margin: "0 0 10px 0" }}>Select Field Type</h4>
                <div
                  style={{ display: "flex", gap: "10px", marginBottom: "20px" }}
                >
                  <button
                    onClick={() => handleFieldTypeSelect("text")}
                    style={{
                      padding: "10px",
                      flex: 1,
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Text Field
                  </button>
                  <button
                    onClick={() => handleFieldTypeSelect("table")}
                    style={{
                      padding: "10px",
                      flex: 1,
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Table
                  </button>
                </div>
              </div>
            )}

            {configStep === "textFieldType" && (
              <div>
                <h4 style={{ margin: "0 0 10px 0" }}>Select Text Field Type</h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginBottom: "20px",
                  }}
                >
                  <button
                    onClick={() => handleTextFieldTypeSelect("text")}
                    style={{
                      padding: "10px",
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Text
                  </button>
                  <button
                    onClick={() => handleTextFieldTypeSelect("number")}
                    style={{
                      padding: "10px",
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Number
                  </button>
                  <button
                    onClick={() => handleTextFieldTypeSelect("date")}
                    style={{
                      padding: "10px",
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Date
                  </button>
                </div>
              </div>
            )}

            {configStep === "columns" && (
              <div>
                <h4 style={{ margin: "0 0 10px 0" }}>
                  Configure Table Columns
                </h4>
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ marginBottom: "10px" }}>
                    <label>Number of Columns:</label>
                    <input
                      type="number"
                      min="2"
                      max="10"
                      value={columnCount}
                      onChange={(e) =>
                        handleColumnCountChange(parseInt(e.target.value))
                      }
                      style={{
                        width: "100%",
                        padding: "8px",
                        marginTop: "5px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      border: "2px dashed #FF6B6B",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      color: "#666",
                    }}
                  >
                    <span>↔️</span> Drag the red bars to adjust column widths
                  </div>
                </div>
                {configuringBox?.columnConfigs?.map((config, index) => {
                  const positions = configuringBox.columnPositions || [];
                  const colCount = configuringBox.columns ?? positions.length;
                  const leftEdges =
                    positions.length === colCount
                      ? positions
                      : [0, ...positions];
                  const pct =
                    leftEdges[index] ?? config.columnWidthPercentage ?? 0;
                  return (
                    <div
                      key={index}
                      style={{
                        marginBottom: "10px",
                        padding: "10px",
                        background: "#f5f5f5",
                        borderRadius: "4px",
                      }}
                    >
                      <div>Column {index + 1}</div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "4px",
                        }}
                      >
                        <label style={{ fontSize: "13px" }}>Start %:</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={index === 0 ? 0 : Math.round(pct)}
                          disabled={index === 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!Number.isNaN(val))
                              handleColumnWidthPercentChange(index, val, true);
                          }}
                          style={{
                            width: "64px",
                            padding: "4px 6px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                          }}
                        />
                        <span style={{ fontSize: "13px", color: "#666" }}>
                          %
                        </span>
                      </div>
                    </div>
                  );
                })}

                <button
                  onClick={() => setConfigStep("columnConfig")}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    marginBottom: "10px",
                  }}
                >
                  Configure Column Types
                </button>
              </div>
            )}

            {configStep === "columnConfig" && (
              <div>
                <h4 style={{ margin: "0 0 10px 0" }}>Configure Column Types</h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginBottom: "20px",
                  }}
                >
                  {configuringBox?.columnConfigs?.map((config, index) => {
                    const positions = configuringBox.columnPositions || [];
                    const colCount = configuringBox.columns ?? positions.length;
                    const leftEdges =
                      positions.length === colCount
                        ? positions
                        : [0, ...positions];
                    const pct =
                      leftEdges[index] ?? config.columnWidthPercentage ?? 0;
                    return (
                      <div
                        key={index}
                        style={{
                          padding: "10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                        }}
                      >
                        <div style={{ marginBottom: "8px" }}>
                          <label>Column {index + 1} Name:</label>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "4px",
                            }}
                          >
                            <label style={{ fontSize: "13px" }}>Start %:</label>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              value={index === 0 ? 0 : Math.round(pct)}
                              disabled={index === 0}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!Number.isNaN(val))
                                  handleColumnWidthPercentChange(
                                    index,
                                    val,
                                    true,
                                  );
                              }}
                              style={{
                                width: "64px",
                                padding: "4px 6px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                              }}
                            />
                            <span style={{ fontSize: "13px", color: "#666" }}>
                              %
                            </span>
                          </div>
                          <input
                            type="text"
                            value={config.name}
                            onChange={(e) =>
                              handleColumnConfigChange(
                                index,
                                "name",
                                e.target.value,
                              )
                            }
                            style={{
                              width: "100%",
                              padding: "4px",
                              marginTop: "4px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                            }}
                          />
                        </div>
                        <div>
                          <label>Type:</label>
                          <select
                            value={config.type}
                            onChange={(e) => {
                              const value = e.target?.value;
                              if (!value) return;
                              setBoxes((prev) =>
                                prev.map((box) =>
                                  box.id === selectedBoxId && box.columnConfigs
                                    ? {
                                        ...box,
                                        columnConfigs: box.columnConfigs.map(
                                          (col, i) =>
                                            i === index
                                              ? {
                                                  ...col,
                                                  type: value as TextFieldType,
                                                }
                                              : col,
                                        ),
                                      }
                                    : box,
                                ),
                              );
                              schedulePersistTemplate();
                            }}
                            style={{
                              width: "100%",
                              padding: "4px",
                              marginTop: "4px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                            }}
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => setConfigStep("pageConfig")}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    marginBottom: "10px",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Rectangle Sidebar - in flow next to document to avoid large gap on big screens */}
        <div
          style={{
            flexShrink: 0,
            width: sidebarCollapsed ? 48 : 300,
            maxHeight: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            minWidth: 0,
            overflow: "visible",
          }}
        >
          {/* Sidebar content - only when open; pointer-events auto so content is clickable */}
          {!sidebarCollapsed && (
            <div
              style={{
                width: "300px",
                maxHeight: "calc(100vh - 100px)",
                background: "#f5f5f5",
                border: "1px solid #ccc",
                borderRadius: "8px",
                marginRight: "8px",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                pointerEvents: "auto",
              }}
            >
              {/* Compact close button at top of drawer */}
              <div
                style={{
                  flexShrink: 0,
                  padding: "8px 12px",
                  borderBottom: "1px solid #e0e0e0",
                  background: "#fafafa",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={() => setSidebarCollapsed(true)}
                  style={{
                    width: "32px",
                    height: "32px",
                    padding: 0,
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    background: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Close fields panel"
                >
                  <FontAwesomeIcon icon="chevron-right" />
                </button>
              </div>
              {boxes.length === 0 && (
                <div
                  style={{
                    flexShrink: 0,
                    padding: "12px 20px",
                    borderBottom: "1px solid #e0e0e0",
                    background: "#e7f3ff",
                    fontSize: "12px",
                    color: "#0c5460",
                  }}
                >
                  <strong>Add fields:</strong> Use &quot;Add field&quot; in the
                  top bar (describe in words), or draw a box on the document for
                  a fixed location.
                </div>
              )}

              {/* Scrollable area: field list, extracted data preview, field config */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "16px 20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <h3>Fields</h3>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "20px",
                    }}
                  >
                    <Dropdown
                      onSelect={(id: string | null) =>
                        id != null && handleBoxSelect(id)
                      }
                      className="flex-grow-1"
                    >
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        id="field-select-dropdown"
                        style={{
                          width: "100%",
                          textAlign: "left",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {selectedBoxId
                          ? (() => {
                              const box = visibleBoxes.find(
                                (b) => b.id === selectedBoxId,
                              );
                              if (!box) return "Select a field";
                              const name =
                                box.name || `Field ${box.id.slice(-4)}`;
                              const pageLabel = box.appliesToAllPages
                                ? " (Any page)"
                                : ` (Page ${box.pageNumber})`;
                              return (
                                <>
                                  {box.fieldType === "aiPrompt" ? (
                                    <FontAwesomeIcon
                                      icon="magic"
                                      className="mr-2"
                                    />
                                  ) : box.fieldType === "table" ? (
                                    <FontAwesomeIcon
                                      icon="table"
                                      className="mr-2"
                                    />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon="font"
                                      className="mr-2"
                                    />
                                  )}
                                  {name}
                                  {pageLabel}
                                </>
                              );
                            })()
                          : "Select a field"}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{ maxHeight: "280px", overflowY: "auto" }}
                      >
                        {visibleBoxes.map((box) => (
                          <Dropdown.Item
                            key={box.id}
                            eventKey={box.id}
                            active={box.id === selectedBoxId}
                          >
                            {box.fieldType === "aiPrompt" ? (
                              <FontAwesomeIcon icon="magic" className="mr-2" />
                            ) : box.fieldType === "table" ? (
                              <FontAwesomeIcon icon="table" className="mr-2" />
                            ) : (
                              <FontAwesomeIcon icon="font" className="mr-2" />
                            )}
                            {box.name || `Field ${box.id.slice(-4)}`}
                            {box.appliesToAllPages
                              ? " (Any page)"
                              : ` (Page ${box.pageNumber})`}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <button
                      onClick={handleDeleteBox}
                      disabled={!selectedBoxId || configStep !== "complete"}
                      style={{
                        padding: "6px 12px",
                        // background: "#FF6B6B",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        opacity:
                          !selectedBoxId || configStep !== "complete" ? 0.5 : 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>🗑️</span>
                    </button>
                  </div>

                  {/* --- Extracted data preview (separate from field config) --- */}
                  {selectedBox && (
                    <div
                      style={{
                        marginBottom: "20px",
                        padding: "16px 20px",
                        background: "linear-gradient(135deg, #f0f9ff 0%, #e8f4fd 100%)",
                        border: "1px solid #b6d4fe",
                        borderRadius: "10px",
                        boxShadow: "0 2px 8px rgba(13, 110, 253, 0.08)",
                        borderLeft: "4px solid var(--bs-primary, #0d6efd)",
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "15px",
                          fontWeight: 700,
                          marginBottom: "12px",
                          color: "#0a58ca",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <FontAwesomeIcon icon="file-alt" style={{ opacity: 0.9 }} />
                        Extracted data preview
                      </h4>
                      {selectedBox.fieldType !== "aiPrompt" && (
                        <div style={{ marginBottom: "0" }}>
                          {isLoadingData ? (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                padding: "20px",
                              }}
                            >
                              <ClipLoader
                                loading={true}
                                size={20}
                                color="#4CAF50"
                              />
                            </div>
                          ) : (
                            <pre
                              style={{
                                background: "rgba(255,255,255,0.95)",
                                padding: "12px 14px",
                                borderRadius: "6px",
                                fontSize: "13px",
                                overflow: "auto",
                                maxHeight: "280px",
                                marginTop: "8px",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                border: "1px solid rgba(13, 110, 253, 0.15)",
                              }}
                            >
                              {(() => {
                                try {
                                  const parsed = JSON.parse(
                                    extractedData || "",
                                  );
                                  const text = parsed?.extractedText;
                                  const isTable = [
                                    "table",
                                    "ocrtables",
                                  ].includes(selectedBox?.fieldType || "");

                                  if (isTable) {
                                    const rows = parseCsvToRowsAndColumn(
                                      text || "",
                                    );
                                    return (
                                      <div>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: "10px",
                                            marginBottom: "10px",
                                          }}
                                        >
                                          <CopyToClipboard
                                            text={rows
                                              .map((row) => row.join(","))
                                              .join("\n")}
                                          >
                                            <Button
                                              variant="outline-secondary"
                                              size="sm"
                                            >
                                              <FontAwesomeIcon
                                                icon="copy"
                                                className="mr-1"
                                              />
                                              Copy CSV
                                            </Button>
                                          </CopyToClipboard>
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                              const csvContent = rows
                                                .map((row) => row.join(","))
                                                .join("\n");
                                              const blob = new Blob(
                                                [csvContent],
                                                {
                                                  type: "text/csv",
                                                },
                                              );
                                              const url =
                                                window.URL.createObjectURL(
                                                  blob,
                                                );
                                              const a =
                                                document.createElement("a");
                                              a.href = url;
                                              a.download = `${
                                                selectedBox?.name ||
                                                "extracted_data"
                                              }.csv`;
                                              document.body.appendChild(a);
                                              a.click();
                                              window.URL.revokeObjectURL(url);
                                              document.body.removeChild(a);
                                            }}
                                          >
                                            <FontAwesomeIcon
                                              icon="download"
                                              className="mr-1"
                                            />
                                            Download CSV
                                          </Button>
                                        </div>
                                        <div
                                          style={{
                                            maxHeight: "400px",
                                            overflow: "auto",
                                          }}
                                        >
                                          <CsvToHtmlTable
                                            rows={rows}
                                            csvDelimiter=","
                                            tableClassName="table table-striped table-hover"
                                            tableRowClassName=""
                                            tableColumnClassName=""
                                            hasHeader={true}
                                          />
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: "10px",
                                            marginBottom: "10px",
                                          }}
                                        >
                                          <CopyToClipboard text={text || ""}>
                                            <Button
                                              variant="outline-secondary"
                                              size="sm"
                                            >
                                              <FontAwesomeIcon
                                                icon="copy"
                                                className="mr-1"
                                              />
                                              Copy
                                            </Button>
                                          </CopyToClipboard>
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                              const blob = new Blob(
                                                [text || ""],
                                                {
                                                  type: "text/plain",
                                                },
                                              );
                                              const url =
                                                window.URL.createObjectURL(
                                                  blob,
                                                );
                                              const a =
                                                document.createElement("a");
                                              a.href = url;
                                              a.download = `${
                                                selectedBox?.name ||
                                                "extracted_data"
                                              }.txt`;
                                              document.body.appendChild(a);
                                              a.click();
                                              window.URL.revokeObjectURL(url);
                                              document.body.removeChild(a);
                                            }}
                                          >
                                            <FontAwesomeIcon
                                              icon="download"
                                              className="mr-1"
                                            />
                                            Download
                                          </Button>
                                        </div>
                                        <pre
                                          style={{
                                            background: "rgba(255,255,255,0.95)",
                                            padding: "12px 14px",
                                            borderRadius: "6px",
                                            fontSize: "13px",
                                            overflow: "auto",
                                            maxHeight: "280px",
                                            marginTop: "8px",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            border: "1px solid rgba(13, 110, 253, 0.15)",
                                          }}
                                        >
                                          {text ? text.trim() : extractedData}
                                        </pre>
                                      </div>
                                    );
                                  }
                                } catch {
                                  return (
                                    <div>
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          gap: "10px",
                                          marginBottom: "10px",
                                        }}
                                      >
                                        <CopyToClipboard
                                          text={extractedData || ""}
                                        >
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                          >
                                            <FontAwesomeIcon
                                              icon="copy"
                                              className="mr-1"
                                            />
                                            Copy
                                          </Button>
                                        </CopyToClipboard>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          onClick={() => {
                                            const blob = new Blob(
                                              [extractedData || ""],
                                              {
                                                type: "text/plain",
                                              },
                                            );
                                            const url =
                                              window.URL.createObjectURL(blob);
                                            const a =
                                              document.createElement("a");
                                            a.href = url;
                                            a.download = `${
                                              selectedBox?.name ||
                                              "extracted_data"
                                            }.txt`;
                                            document.body.appendChild(a);
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            document.body.removeChild(a);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon="download"
                                            className="mr-1"
                                          />
                                          Download
                                        </Button>
                                      </div>
                                      <pre
                                        style={{
                                          background: "white",
                                          padding: "10px",
                                          borderRadius: "4px",
                                          fontSize: "12px",
                                          overflow: "auto",
                                          maxHeight: "200px",
                                          marginTop: "8px",
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {extractedData}
                                      </pre>
                                    </div>
                                  );
                                }
                              })()}
                            </pre>
                          )}
                        </div>
                      )}
                      {selectedBox?.fieldType === "aiPrompt" && (
                        <div style={{ marginBottom: "0" }}>
                          {!selectedBox.aiPrompt?.prompt?.trim() ? (
                            <p className="text-muted small mt-0 mb-0">
                              Enter a prompt in Field config below and click
                              Preview field to see the result.
                            </p>
                          ) : aiPreviewLoading ? (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                padding: "20px",
                              }}
                            >
                              <ClipLoader
                                loading={true}
                                size={20}
                                color="#4CAF50"
                              />
                            </div>
                          ) : aiPreviewError ? (
                            <p className="text-danger small mt-2 mb-0">
                              {aiPreviewError}
                            </p>
                          ) : (
                            (() => {
                              const text =
                                aiPreviewText != null
                                  ? aiPreviewText.trim()
                                  : "";
                              const isAiTableField =
                                (selectedBox.columns ?? 0) > 0 ||
                                (selectedBox.columnConfigs?.length ?? 0) > 0;
                              // Infer table rows from data: try CSV first, then JSON array of arrays (columns come from data)
                              let rows: string[][] = [];
                              if (text && isAiTableField) {
                                rows = parseCsvToRowsAndColumn(text);
                                if (rows.length === 0 && text.startsWith("[")) {
                                  try {
                                    const parsed = JSON.parse(text);
                                    if (
                                      Array.isArray(parsed) &&
                                      parsed.length > 0
                                    ) {
                                      rows = parsed.map((row: unknown) =>
                                        Array.isArray(row)
                                          ? row.map((c: unknown) =>
                                              String(c ?? ""),
                                            )
                                          : [String(row)],
                                      );
                                    }
                                  } catch {
                                    // leave rows empty
                                  }
                                }
                              }
                              if (rows.length > 0) {
                                return (
                                  <div style={{ marginTop: "8px" }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "10px",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <CopyToClipboard
                                        text={rows
                                          .map((row) => row.join(","))
                                          .join("\n")}
                                      >
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                        >
                                          <FontAwesomeIcon
                                            icon="copy"
                                            className="mr-1"
                                          />
                                          Copy CSV
                                        </Button>
                                      </CopyToClipboard>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                          const csvContent = rows
                                            .map((row) => row.join(","))
                                            .join("\n");
                                          const blob = new Blob([csvContent], {
                                            type: "text/csv",
                                          });
                                          const url =
                                            window.URL.createObjectURL(blob);
                                          const a = document.createElement("a");
                                          a.href = url;
                                          a.download = `${selectedBox?.name || "extracted_data"}.csv`;
                                          document.body.appendChild(a);
                                          a.click();
                                          window.URL.revokeObjectURL(url);
                                          document.body.removeChild(a);
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon="download"
                                          className="mr-1"
                                        />
                                        Download CSV
                                      </Button>
                                    </div>
                                    <div
                                      style={{
                                        maxHeight: "400px",
                                        overflow: "auto",
                                      }}
                                    >
                                      <CsvToHtmlTable
                                        rows={rows}
                                        csvDelimiter=","
                                        tableClassName="table table-striped table-hover"
                                        tableRowClassName=""
                                        tableColumnClassName=""
                                        hasHeader={true}
                                      />
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <pre
                                  style={{
                                    background: "white",
                                    padding: "10px",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    overflow: "auto",
                                    maxHeight: "200px",
                                    marginTop: "8px",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {text || "—"}
                                </pre>
                              );
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* --- Field config (separate from preview) --- */}
                  {selectedBox && (
                    <div
                      className="p-3 bg-light rounded border"
                    >
                      <h5 className="mb-3 font-weight-bold text-secondary small">
                        Field config
                      </h5>
                      <Form.Group className="mb-3">
                        <Form.Label className="small font-weight-bold">Name</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={selectedBox.name || ""}
                          onChange={handleNameChange}
                          className="mt-1"
                        />
                      </Form.Group>
                      {selectedBox.fieldType === "aiPrompt" ? (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label className="small font-weight-bold">What to extract</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              size="sm"
                              value={selectedBox.aiPrompt?.prompt ?? ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                setBoxes((prev) =>
                                  prev.map((box) =>
                                    box.id === selectedBoxId
                                      ? { ...box, aiPrompt: { prompt: value } }
                                      : box,
                                  ),
                                );
                                schedulePersistTemplate();
                              }}
                              placeholder="Describe what to extract..."
                              className="mt-1"
                            />
                            <div className="mt-2 d-flex align-items-center flex-wrap">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={
                                  !selectedBox.aiPrompt?.prompt?.trim() ||
                                  aiPreviewLoading
                                }
                                onClick={() =>
                                  fetchAiPromptPreview(selectedBox)
                                }
                                title="Preview extraction for this field"
                              >
                                {aiPreviewLoading ? (
                                  <ClipLoader size={14} />
                                ) : (
                                  <FontAwesomeIcon
                                    icon="eye"
                                    className="mr-1"
                                  />
                                )}
                                <span>
                                  {aiPreviewLoading
                                    ? "Loading…"
                                    : "Preview field"}
                                </span>
                              </Button>
                            </div>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label className="small font-weight-bold">Field type</Form.Label>
                            <Form.Control
                              as="select"
                              size="sm"
                              value={selectedBox.columns ? "table" : "text"}
                              onChange={(e) => {
                                const isTable = e.target.value === "table";
                                setBoxes((prev) =>
                                  prev.map((box) =>
                                    box.id === selectedBoxId
                                      ? {
                                          ...box,
                                          columns: isTable ? 2 : undefined,
                                          columnPositions: isTable
                                            ? [50]
                                            : undefined,
                                          columnConfigs: isTable
                                            ? [
                                                {
                                                  name: "Column 1",
                                                  type: "text" as TextFieldType,
                                                  columnWidthPercentage: 0,
                                                },
                                                {
                                                  name: "Column 2",
                                                  type: "text" as TextFieldType,
                                                  columnWidthPercentage: 50,
                                                },
                                              ]
                                            : undefined,
                                        }
                                      : box,
                                  ),
                                );
                                schedulePersistTemplate();
                              }}
                              className="mt-1"
                            >
                              <option value="text">Text</option>
                              <option value="table">Table</option>
                            </Form.Control>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label className="small font-weight-bold d-block">Page</Form.Label>
                            <div className="mt-1">
                              <Form.Check
                                type="radio"
                                id="ai-page-current"
                                name="ai-page-scope"
                                label="Current page only"
                                checked={!selectedBox.appliesToAllPages}
                                onChange={() => {
                                  setBoxes((prev) =>
                                    prev.map((box) =>
                                      box.id === selectedBoxId
                                        ? {
                                            ...box,
                                            appliesToAllPages: false,
                                            pageNumber: currentPage,
                                          }
                                        : box,
                                    ),
                                  );
                                  schedulePersistTemplate();
                                }}
                              />
                              <Form.Check
                                type="radio"
                                id="ai-page-any"
                                name="ai-page-scope"
                                label="Any page"
                                checked={selectedBox.appliesToAllPages === true}
                                onChange={() => {
                                  setBoxes((prev) =>
                                    prev.map((box) =>
                                      box.id === selectedBoxId
                                        ? {
                                            ...box,
                                            appliesToAllPages: true,
                                            pageNumber: undefined,
                                          }
                                        : box,
                                    ),
                                  );
                                  schedulePersistTemplate();
                                }}
                              />
                            </div>
                          </Form.Group>
                        </>
                      ) : (
                        <>
                          {false && (
                            <div style={{ marginBottom: "10px" }}>
                              <strong>Type:</strong>{" "}
                              {selectedBox?.fieldType === "table"
                                ? "Table"
                                : selectedBox?.textFieldType === "number"
                                  ? "Number"
                                  : selectedBox?.textFieldType === "date"
                                    ? "Date"
                                    : "Text"}
                            </div>
                          )}

                          {selectedBox.fieldType === "text" && (
                            <Form.Group className="mb-3">
                              <Form.Label className="small font-weight-bold">Text field type</Form.Label>
                              <Form.Control
                                as="select"
                                size="sm"
                                value={selectedBox.textFieldType || "text"}
                                onChange={(e) => {
                                  const newType = e.target
                                    .value as TextFieldType;
                                  setBoxes((prev) =>
                                    prev.map((box) =>
                                      box.id === selectedBoxId
                                        ? { ...box, textFieldType: newType }
                                        : box,
                                    ),
                                  );
                                  schedulePersistTemplate();
                                }}
                                className="mt-1"
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="date">Date</option>
                              </Form.Control>
                            </Form.Group>
                          )}

                          {selectedBox.fieldType === "table" && (
                            <>
                              <Form.Group className="mb-3">
                                <Form.Label className="small font-weight-bold">Number of columns</Form.Label>
                                <Form.Control
                                  type="number"
                                  min={2}
                                  max={10}
                                  size="sm"
                                  value={selectedBox.columns || 2}
                                  onChange={(e) => {
                                    const newCount = Math.max(
                                      2,
                                      Math.min(
                                        10,
                                        parseInt(e.target.value) || 2,
                                      ),
                                    );
                                    const positions = [
                                      0,
                                      ...Array.from(
                                        { length: newCount - 1 },
                                        (_, i) => ((i + 1) * 100) / newCount,
                                      ),
                                    ];
                                    setBoxes((prev) =>
                                      prev.map((box) =>
                                        box.id === selectedBoxId
                                          ? {
                                              ...box,
                                              columns: newCount,
                                              columnPositions: positions,
                                              columnConfigs: Array(newCount)
                                                .fill(null)
                                                .map((_, i) => ({
                                                  name:
                                                    box.columnConfigs?.[i]
                                                      ?.name ||
                                                    `Column ${i + 1}`,
                                                  type:
                                                    box.columnConfigs?.[i]
                                                      ?.type || "text",
                                                  columnWidthPercentage:
                                                    positions[i],
                                                })),
                                            }
                                          : box,
                                      ),
                                    );
                                    schedulePersistTemplate();
                                  }}
                                  className="mt-1"
                                />
                              </Form.Group>

                              {selectedBox.columnConfigs &&
                                selectedBox.columnConfigs.length > 0 && (
                                  <Form.Group className="mb-3">
                                    <Form.Label className="small font-weight-bold d-block">Column configuration</Form.Label>
                                    <div className="d-flex flex-column gap-2 mt-1">
                                      {selectedBox.columnConfigs.map(
                                        (config, index) => {
                                          const positions =
                                            selectedBox.columnPositions || [];
                                          const colCount =
                                            selectedBox.columns ??
                                            positions.length;
                                          const leftEdges =
                                            positions.length === colCount
                                              ? positions
                                              : [0, ...positions];
                                          const pct =
                                            leftEdges[index] ??
                                            config.columnWidthPercentage ??
                                            0;
                                          return (
                                            <div
                                              key={index}
                                              className="p-2 border rounded"
                                            >
                                              <div className="mb-2">
                                                <Form.Label className="small mb-0">Column {index + 1} name</Form.Label>
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                  <Form.Label className="small text-muted mb-0 mr-1">Start %</Form.Label>
                                                  <Form.Control
                                                    type="number"
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    size="sm"
                                                    style={{ width: "64px" }}
                                                    value={
                                                      index === 0
                                                        ? 0
                                                        : Math.round(pct)
                                                    }
                                                    disabled={index === 0}
                                                    onChange={(e) => {
                                                      const val = parseFloat(
                                                        e.target.value,
                                                      );
                                                      if (!Number.isNaN(val))
                                                        handleColumnWidthPercentChange(
                                                          index,
                                                          val,
                                                          false,
                                                        );
                                                    }}
                                                  />
                                                  <span className="small text-muted">%</span>
                                                </div>
                                                <Form.Control
                                                  type="text"
                                                  size="sm"
                                                  value={config.name}
                                                  onChange={(e) => {
                                                    const value =
                                                      e.target?.value;
                                                    if (value === undefined)
                                                      return;
                                                    setBoxes((prev) =>
                                                      prev.map((box) =>
                                                        box.id === selectedBoxId
                                                          ? {
                                                              ...box,
                                                              columnConfigs:
                                                                box.columnConfigs?.map(
                                                                  (col, i) =>
                                                                    i === index
                                                                      ? {
                                                                          ...col,
                                                                          name: value,
                                                                        }
                                                                      : col,
                                                                ),
                                                            }
                                                          : box,
                                                      ),
                                                    );
                                                    schedulePersistTemplate();
                                                  }}
                                                  className="mt-1"
                                                />
                                              </div>
                                              <Form.Group>
                                                <Form.Label className="small mb-0">Type</Form.Label>
                                                <Form.Control
                                                  as="select"
                                                  size="sm"
                                                  value={config.type}
                                                  onChange={(e) => {
                                                    const value =
                                                      e.target?.value;
                                                    if (!value) return;
                                                    setBoxes((prev) =>
                                                      prev.map((box) =>
                                                        box.id ===
                                                          selectedBoxId &&
                                                        box.columnConfigs
                                                          ? {
                                                              ...box,
                                                              columnConfigs:
                                                                box.columnConfigs.map(
                                                                  (col, i) =>
                                                                    i === index
                                                                      ? {
                                                                          ...col,
                                                                          type: value as TextFieldType,
                                                                        }
                                                                      : col,
                                                                ),
                                                            }
                                                          : box,
                                                      ),
                                                    );
                                                    schedulePersistTemplate();
                                                  }}
                                                  className="mt-1"
                                                >
                                                  <option value="text">
                                                    Text
                                                  </option>
                                                  <option value="number">
                                                    Number
                                                  </option>
                                                  <option value="date">
                                                    Date
                                                  </option>
                                                </Form.Control>
                                              </Form.Group>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  </Form.Group>
                                )}
                            </>
                          )}
                          {false && (
                            <>
                              <div style={{ marginBottom: "10px" }}>
                                <strong>Position:</strong>
                                <div style={{ marginLeft: "10px" }}>
                                  X: {Math.round(selectedBox?.x || 0)}px
                                  <br />
                                  Y: {Math.round(selectedBox?.y || 0)}px
                                </div>
                              </div>
                              <div style={{ marginBottom: "10px" }}>
                                <strong>Dimensions:</strong>
                                <div style={{ marginLeft: "10px" }}>
                                  Width: {Math.round(selectedBox?.width || 0)}px
                                  <br />
                                  Height: {Math.round(selectedBox?.height || 0)}
                                  px
                                </div>
                              </div>
                              <div style={{ marginBottom: "10px" }}>
                                <strong>Area:</strong>{" "}
                                {Math.round(
                                  (selectedBox?.width || 0) *
                                    (selectedBox?.height || 0),
                                )}
                                px²
                              </div>
                              <div style={{ marginBottom: "10px" }}>
                                <strong>Page:</strong>{" "}
                                {selectedBox?.appliesToAllPages
                                  ? "Any page"
                                  : `Page ${selectedBox?.pageNumber}`}
                              </div>
                            </>
                          )}
                        </>
                      )}
                      {/* <div style={{ marginBottom: "20px" }}>
                <button
                  onClick={() => {
                    const selectedBox = boxes.find(
                      (box) => box.id === selectedBoxId
                    );
                    fetchRectangleData(selectedBox);
                  }}
                  disabled={isLoadingData}
                  style={{
                    width: "100%",
                    padding: "8px",
                    background: isLoadingData ? "#ccc" : "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isLoadingData ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {isLoadingData ? <>Loading...</> : <>🔍 Extract Data</>}
                </button>
              </div> */}

                      {selectedBox.fieldType !== "aiPrompt" && (
                        <Form.Group className="mb-0">
                          <Form.Label className="small font-weight-bold d-block">Page</Form.Label>
                          <div className="d-flex flex-column gap-2 mt-1">
                            <div
                              className={`p-2 border rounded ${!selectedBox.appliesToAllPages ? "bg-light" : ""}`}
                            >
                              <Form.Check
                                type="radio"
                                id="page-current-only"
                                name="page-scope"
                                label={<span className="font-weight-bold">Current page only</span>}
                                checked={selectedBox?.appliesToAllPages === false}
                                onChange={() => {
                                  setBoxes((prev) =>
                                    prev.map((box) =>
                                      box.id === selectedBoxId
                                        ? {
                                            ...box,
                                            appliesToAllPages: false,
                                            pageNumber: currentPage,
                                          }
                                        : box,
                                    ),
                                  );
                                  schedulePersistTemplate();
                                }}
                              />
                              <div className="small text-muted ml-4 mt-0">
                                This field will only appear on page{" "}
                                {currentPage}. Use this for fields that are
                                unique to this page.
                              </div>
                            </div>
                            <div
                              className={`p-2 border rounded ${selectedBox.appliesToAllPages ? "bg-light" : ""}`}
                            >
                              <Form.Check
                                type="radio"
                                id="page-any"
                                name="page-scope"
                                label={<span className="font-weight-bold">Any page</span>}
                                checked={selectedBox?.appliesToAllPages === true}
                                onChange={() => {
                                  setBoxes((prev) =>
                                    prev.map((box) =>
                                      box.id === selectedBoxId
                                        ? {
                                            ...box,
                                            appliesToAllPages: true,
                                            pageNumber: undefined,
                                          }
                                        : box,
                                    ),
                                  );
                                  schedulePersistTemplate();
                                }}
                              />
                              <div className="small text-muted ml-4 mt-0">
                                This field will appear on every page of the
                                document. Use this for fields that repeat across
                                pages.
                              </div>
                            </div>
                          </div>
                        </Form.Group>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {false && (
                <div>
                  <h4>Debug Info</h4>
                  <pre
                    style={{
                      background: "white",
                      padding: "10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      overflow: "auto",
                    }}
                  >
                    {JSON.stringify(
                      {
                        isDrawing,
                        startPos,
                        drawingBox: drawingBoxRef.current,
                        boxesCount: visibleBoxes.length,
                        configStep,
                        configuringBox,
                      },
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
            </div>
          )}
          {/* Open button - fixed position so never cut off by parent overflow; pointer-events auto */}
          {sidebarCollapsed && (
            <button
              type="button"
              onClick={() => setSidebarCollapsed(false)}
              style={{
                position: "fixed",
                right: "20px",
                top: "88px",
                width: "40px",
                height: "40px",
                padding: 0,
                border: "1px solid #ccc",
                borderRadius: "8px",
                background: "#f5f5f5",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                pointerEvents: "auto",
              }}
              title="Open fields panel"
            >
              <FontAwesomeIcon icon="chevron-left" />
            </button>
          )}
        </div>
      </div>

      <Modal
        show={showExtractionWarningModal}
        onHide={() => {
          setShowExtractionWarningModal(false);
          setPendingExtractionAction(null);
        }}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title">Before you start extraction</h5>
          <Button
            variant="link"
            className="p-0 ml-auto"
            onClick={() => {
              setShowExtractionWarningModal(false);
              setPendingExtractionAction(null);
            }}
            aria-label="Close"
          >
            ×
          </Button>
        </div>
        <Modal.Body>
          <p className="mb-0">
            Make sure all fields are defined and the previewed data looks good.
            You can select each field and use &quot;Extract Data&quot; or
            &quot;Preview field&quot; to verify before running extraction.
          </p>
          <p className="mb-0 mt-2 text-muted small">
            Do you want to proceed with extraction?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowExtractionWarningModal(false);
              setPendingExtractionAction(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={runPendingExtraction}>
            Proceed
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddAiModal}
        onHide={() => setShowAddAiModal(false)}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title">Add field</h5>
          <Button
            variant="link"
            className="p-0 ml-auto"
            onClick={() => setShowAddAiModal(false)}
            aria-label="Close"
          >
            ×
          </Button>
        </div>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Field name</Form.Label>
            <Form.Control
              type="text"
              value={aiFieldForm.name}
              onChange={(e) => {
                const value = e.target.value;
                setAiFieldForm((f) => ({ ...f, name: value }));
              }}
              placeholder="e.g. Total amount"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>What do you want to pull from the document?</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={aiFieldForm.prompt}
              onChange={(e) => {
                const value = e.target.value;
                setAiFieldForm((f) => ({ ...f, prompt: value }));
              }}
              placeholder="Describe what to extract, e.g. Extract the total amount due from the invoice"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Field type</Form.Label>
            <Form.Control
              as="select"
              value={aiFieldForm.dataType}
              onChange={(e) => {
                const value = e.target.value as "text" | "table";
                setAiFieldForm((f) => ({ ...f, dataType: value }));
              }}
            >
              <option value="text">Text</option>
              <option value="table">Table</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Page</Form.Label>
            <Form.Control
              as="select"
              value={aiFieldForm.appliesToAllPages ? "all" : "current"}
              onChange={(e) => {
                const value = e.target.value;
                setAiFieldForm((f) => ({
                  ...f,
                  appliesToAllPages: value === "all",
                }));
              }}
            >
              <option value="current">Current page only</option>
              <option value="all">Any page</option>
            </Form.Control>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddAiModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => handleAddAiField(aiFieldForm)}
            disabled={!aiFieldForm.name.trim() || !aiFieldForm.prompt.trim()}
          >
            Add field
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// Export the component directly
export const DrawRectangleV3 = DrawRectangleV3_;

// Export a simple connected component for testing
export const DrawRectangleV3Connected: React.FC = () => {
  return <DrawRectangleV3 />;
};

export default DrawRectangleV3Connected;
